import * as React from 'react';
import React__default, { createContext, useContext, useRef, useState, useEffect, Children, isValidElement, useMemo, memo, PureComponent, Component, createRef, useCallback, useLayoutEffect, Fragment, forwardRef } from 'react';
import { createPortal } from 'react-dom';

const modifiers = {
    prefix: '',
};
const BhThemeContext = createContext(modifiers);
const BhThemeProvider = ({ children, theme, }) => {
    if (!theme) {
        throw Error('[BhThemeProvider]: the `theme` prop is required');
    }
    return (React__default.createElement(BhThemeContext.Provider, { value: theme }, children));
};
function useTheme() {
    const theme = useContext(BhThemeContext);
    if (!theme) {
        throw Error('useTheme: `theme` is undefined. Seems you forgot to wrap your app in `<ChakraProvider />`');
    }
    return theme;
}
const BhThemeConsumer = BhThemeContext.Consumer;

var classnames = {exports: {}};

(function (module) {

(function () {

	var hasOwn = {}.hasOwnProperty;

	function classNames() {
		var classes = [];

		for (var i = 0; i < arguments.length; i++) {
			var arg = arguments[i];
			if (!arg) continue;

			var argType = typeof arg;

			if (argType === 'string' || argType === 'number') {
				classes.push(arg);
			} else if (Array.isArray(arg)) {
				if (arg.length) {
					var inner = classNames.apply(null, arg);
					if (inner) {
						classes.push(inner);
					}
				}
			} else if (argType === 'object') {
				if (arg.toString === Object.prototype.toString) {
					for (var key in arg) {
						if (hasOwn.call(arg, key) && arg[key]) {
							classes.push(key);
						}
					}
				} else {
					classes.push(arg.toString());
				}
			}
		}

		return classes.join(' ');
	}

	if (module.exports) {
		classNames.default = classNames;
		module.exports = classNames;
	} else {
		window.classNames = classNames;
	}
}());
}(classnames));

var classNames = classnames.exports;

const deriveClassName = (onlyIcon, singleLine, variant, noTransform, className, light, size, triangle, arrow, special, color, fullWidth, minWidth, prefixedText, preactClass) => classNames(`${prefixedText}c-btn`, {
    [`${prefixedText}c-btn--primary`]: variant === 'primary',
}, {
    [`${prefixedText}c-btn--iconTri ${prefixedText}tri-right`]: triangle === 'right',
}, {
    [`${prefixedText}c-btn--iconTri ${prefixedText}tri-bottom`]: triangle === 'bottom',
}, {
    [`${prefixedText}c-btn--iconTri ${prefixedText}tri-left`]: triangle === 'left',
}, {
    [`${prefixedText}arrow ${prefixedText}arrow-right`]: arrow === 'right',
}, {
    [`${prefixedText}arrow ${prefixedText}arrow-bottom`]: arrow === 'bottom',
}, {
    [`${prefixedText}arrow--left ${prefixedText}arrow-left`]: arrow === 'left',
}, {
    [`${prefixedText}c-btn--facebook`]: special === 'facebook',
}, {
    [`${prefixedText}c-btn--twitter`]: special === 'twitter',
}, {
    [`${prefixedText}c-btn--gPlus`]: special === 'gplus',
}, {
    [`${prefixedText}c-btn--blue`]: color === 'blue',
}, {
    [`${prefixedText}c-btn--orange`]: color === 'orange',
}, {
    [`${prefixedText}c-btn--green`]: color === 'green',
}, {
    [`${prefixedText}c-btn--violet`]: color === 'violet',
}, {
    [`${prefixedText}c-btn--white`]: color === 'white',
}, {
    [`${prefixedText}c-btn--blandGray`]: color === 'blandGray',
}, {
    [`${prefixedText}c-btn--fullWidth`]: fullWidth,
}, {
    [`${prefixedText}c-btn--size1`]: size === 'med',
}, {
    [`${prefixedText}c-btn--size3`]: size === 'big',
}, {
    [`${prefixedText}c-btn--size2`]: size === 'small',
}, {
    [`${prefixedText}c-btn--tiny`]: size === 'tiny',
}, {
    [`${prefixedText}c-btn--vtiny`]: size === 'vtiny',
}, {
    [`${prefixedText}c-btn--outline`]: variant === 'outline',
}, {
    [`${prefixedText}c-btn--outline ${prefixedText}c-btn--outline--colored`]: variant === 'outlinecolored',
}, {
    [`${prefixedText}c-btn--minimal`]: variant === 'minimal',
}, {
    [`${prefixedText}c-btn--minimalLight`]: variant === 'minimal' && light,
}, {
    [`${prefixedText}c-btn--iconOnly`]: onlyIcon,
}, {
    [`${prefixedText}u-text--singleLine`]: singleLine,
}, {
    [`${prefixedText}c-btn--link ${prefixedText}c-links ${prefixedText}font-bold`]: variant === 'link',
}, {
    [`${prefixedText}c-btn--noTransform`]: noTransform,
}, {
    [`${prefixedText}c-btn--minWidth`]: minWidth,
}, className, preactClass);
const Button = ({ variant, triangle, arrow, special, color, fullWidth, size, onlyIcon, singleLine, noTransform, className, light, icon, activeClassName, link, href, exact, target, rel, closeToast, children, focus, linkClass, disabled, type = 'button', minWidth, class: preactClass, ...childProps }) => {
    const refComp = useRef(null);
    const { prefix } = useTheme();
    const prefixedText = prefix ? `${prefix}-` : '';
    const [classname, setClassname] = useState(deriveClassName(onlyIcon, singleLine, variant, noTransform, className, light, size, triangle, arrow, special, color, fullWidth, minWidth, prefixedText, preactClass));
    useEffect(() => {

        if (focus === true &&
            refComp.current &&
            typeof refComp.current.focus === 'function') {

            requestAnimationFrame(() => {
                refComp.current.focus();
            });

        }

    }, [focus]);
    useEffect(() => {
        setClassname(deriveClassName(onlyIcon, singleLine, variant, noTransform, className, light, size, triangle, arrow, special, color, fullWidth, minWidth, prefixedText, preactClass));
    }, [
        onlyIcon,
        singleLine,
        variant,
        noTransform,
        className,
        light,
        size,
        triangle,
        arrow,
        special,
        color,
        fullWidth,
        minWidth,
        prefixedText,
    ]);
    if (target === '_blank') {
        if (!rel || !rel.includes('noopener')) {
            throw new Error('Please add atleast rel="noopener" when using _blank');
        }
    }
    const button = (React__default.createElement("button", { ref: refComp, className: classname, disabled: disabled, type: type, ...childProps },
        icon,
        children));
    if (!disabled) {
        if (href) {
            return (React__default.createElement("a", { href: href, tabIndex: -1, role: "button", "aria-label": "Link Button", target: target, rel: rel, className: classname, ...childProps },
                icon,
                children));
        }

    }
    return button;
};

function IfElse(props) {
    const condition = props.condition || false;
    const positive = props.then || null;
    const negative = props.else || null;
    if (condition) {
        if (typeof positive === 'function') {
            return positive();
        }
        return positive;
    }
    if (typeof negative === 'function') {
        return negative();
    }
    return negative;
}
const render = ({ children }) => {
    if (typeof children === 'function') {
        return React__default.createElement(React__default.Fragment, null, children());
    }
    return React__default.createElement(React__default.Fragment, null, children || null);
};
function getConditionResult(condition) {
    const conditionResult = !!(typeof condition === 'function'
        ? condition()
        : condition);
    return conditionResult;
}
const Then = (props) => render(props);
const Else = (props) => render(props);
const If = ({ condition, children }) => {
    if (children == null) {
        return null;
    }
    if (typeof children === 'function') {
        return children(condition);
    }
    return ([]
        .concat(children)
        .find(c => (c.type !== Else) !==
        !getConditionResult(condition)) || null);
};
const Case = (props) => render(props);
const Default = (props) => render(props);
function Switch({ children }) {

    let matchingCase = null;
    let defaultCase = null;
    Children.forEach(children, child => {

        if (!isValidElement(child)) {
            return;
        }
        if (!matchingCase && child.type === Case) {
            const { condition } = child.props;
            const conditionResult = getConditionResult(condition);
            if (conditionResult) {
                matchingCase = child;
            }
        }
        else if (!defaultCase && child.type === Default) {
            defaultCase = child;
        }
    });
    return matchingCase || defaultCase || null;
}

var fromEntries = function fromEntries(entries) {
  return entries.reduce(function (acc, _ref) {
    var key = _ref[0],
        value = _ref[1];
    acc[key] = value;
    return acc;
  }, {});
};

var useIsomorphicLayoutEffect = typeof window !== 'undefined' && window.document && window.document.createElement ? React.useLayoutEffect : React.useEffect;

var top = 'top';
var bottom = 'bottom';
var right = 'right';
var left = 'left';
var auto = 'auto';
var basePlacements = [top, bottom, right, left];
var start = 'start';
var end = 'end';
var clippingParents = 'clippingParents';
var viewport = 'viewport';
var popper = 'popper';
var reference = 'reference';
var variationPlacements = basePlacements.reduce(function (acc, placement) {
  return acc.concat([placement + "-" + start, placement + "-" + end]);
}, []);
var placements = [].concat(basePlacements, [auto]).reduce(function (acc, placement) {
  return acc.concat([placement, placement + "-" + start, placement + "-" + end]);
}, []);

var beforeRead = 'beforeRead';
var read = 'read';
var afterRead = 'afterRead';

var beforeMain = 'beforeMain';
var main = 'main';
var afterMain = 'afterMain';

var beforeWrite = 'beforeWrite';
var write = 'write';
var afterWrite = 'afterWrite';
var modifierPhases = [beforeRead, read, afterRead, beforeMain, main, afterMain, beforeWrite, write, afterWrite];

function getNodeName(element) {
  return element ? (element.nodeName || '').toLowerCase() : null;
}

function getWindow(node) {
  if (node == null) {
    return window;
  }

  if (node.toString() !== '[object Window]') {
    var ownerDocument = node.ownerDocument;
    return ownerDocument ? ownerDocument.defaultView || window : window;
  }

  return node;
}

function isElement(node) {
  var OwnElement = getWindow(node).Element;
  return node instanceof OwnElement || node instanceof Element;
}

function isHTMLElement(node) {
  var OwnElement = getWindow(node).HTMLElement;
  return node instanceof OwnElement || node instanceof HTMLElement;
}

function isShadowRoot(node) {

  if (typeof ShadowRoot === 'undefined') {
    return false;
  }

  var OwnElement = getWindow(node).ShadowRoot;
  return node instanceof OwnElement || node instanceof ShadowRoot;
}

function applyStyles(_ref) {
  var state = _ref.state;
  Object.keys(state.elements).forEach(function (name) {
    var style = state.styles[name] || {};
    var attributes = state.attributes[name] || {};
    var element = state.elements[name];

    if (!isHTMLElement(element) || !getNodeName(element)) {
      return;
    }

    Object.assign(element.style, style);
    Object.keys(attributes).forEach(function (name) {
      var value = attributes[name];

      if (value === false) {
        element.removeAttribute(name);
      } else {
        element.setAttribute(name, value === true ? '' : value);
      }
    });
  });
}

function effect$2(_ref2) {
  var state = _ref2.state;
  var initialStyles = {
    popper: {
      position: state.options.strategy,
      left: '0',
      top: '0',
      margin: '0'
    },
    arrow: {
      position: 'absolute'
    },
    reference: {}
  };
  Object.assign(state.elements.popper.style, initialStyles.popper);
  state.styles = initialStyles;

  if (state.elements.arrow) {
    Object.assign(state.elements.arrow.style, initialStyles.arrow);
  }

  return function () {
    Object.keys(state.elements).forEach(function (name) {
      var element = state.elements[name];
      var attributes = state.attributes[name] || {};
      var styleProperties = Object.keys(state.styles.hasOwnProperty(name) ? state.styles[name] : initialStyles[name]);

      var style = styleProperties.reduce(function (style, property) {
        style[property] = '';
        return style;
      }, {});

      if (!isHTMLElement(element) || !getNodeName(element)) {
        return;
      }

      Object.assign(element.style, style);
      Object.keys(attributes).forEach(function (attribute) {
        element.removeAttribute(attribute);
      });
    });
  };
}

var applyStyles$1 = {
  name: 'applyStyles',
  enabled: true,
  phase: 'write',
  fn: applyStyles,
  effect: effect$2,
  requires: ['computeStyles']
};

function getBasePlacement(placement) {
  return placement.split('-')[0];
}

var max = Math.max;
var min = Math.min;
var round = Math.round;

function getBoundingClientRect(element, includeScale) {
  if (includeScale === void 0) {
    includeScale = false;
  }

  var rect = element.getBoundingClientRect();
  var scaleX = 1;
  var scaleY = 1;

  if (isHTMLElement(element) && includeScale) {
    var offsetHeight = element.offsetHeight;
    var offsetWidth = element.offsetWidth;

    if (offsetWidth > 0) {
      scaleX = round(rect.width) / offsetWidth || 1;
    }

    if (offsetHeight > 0) {
      scaleY = round(rect.height) / offsetHeight || 1;
    }
  }

  return {
    width: rect.width / scaleX,
    height: rect.height / scaleY,
    top: rect.top / scaleY,
    right: rect.right / scaleX,
    bottom: rect.bottom / scaleY,
    left: rect.left / scaleX,
    x: rect.left / scaleX,
    y: rect.top / scaleY
  };
}

function getLayoutRect(element) {
  var clientRect = getBoundingClientRect(element);

  var width = element.offsetWidth;
  var height = element.offsetHeight;

  if (Math.abs(clientRect.width - width) <= 1) {
    width = clientRect.width;
  }

  if (Math.abs(clientRect.height - height) <= 1) {
    height = clientRect.height;
  }

  return {
    x: element.offsetLeft,
    y: element.offsetTop,
    width: width,
    height: height
  };
}

function contains(parent, child) {
  var rootNode = child.getRootNode && child.getRootNode();

  if (parent.contains(child)) {
    return true;
  }
  else if (rootNode && isShadowRoot(rootNode)) {
      var next = child;

      do {
        if (next && parent.isSameNode(next)) {
          return true;
        }

        next = next.parentNode || next.host;
      } while (next);
    }

  return false;
}

function getComputedStyle(element) {
  return getWindow(element).getComputedStyle(element);
}

function isTableElement(element) {
  return ['table', 'td', 'th'].indexOf(getNodeName(element)) >= 0;
}

function getDocumentElement(element) {

  return ((isElement(element) ? element.ownerDocument :
  element.document) || window.document).documentElement;
}

function getParentNode(element) {
  if (getNodeName(element) === 'html') {
    return element;
  }

  return (

    element.assignedSlot ||
    element.parentNode || (
    isShadowRoot(element) ? element.host : null) ||

    getDocumentElement(element)

  );
}

function getTrueOffsetParent(element) {
  if (!isHTMLElement(element) ||
  getComputedStyle(element).position === 'fixed') {
    return null;
  }

  return element.offsetParent;
}

function getContainingBlock(element) {
  var isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') !== -1;
  var isIE = navigator.userAgent.indexOf('Trident') !== -1;

  if (isIE && isHTMLElement(element)) {

    var elementCss = getComputedStyle(element);

    if (elementCss.position === 'fixed') {
      return null;
    }
  }

  var currentNode = getParentNode(element);

  while (isHTMLElement(currentNode) && ['html', 'body'].indexOf(getNodeName(currentNode)) < 0) {
    var css = getComputedStyle(currentNode);

    if (css.transform !== 'none' || css.perspective !== 'none' || css.contain === 'paint' || ['transform', 'perspective'].indexOf(css.willChange) !== -1 || isFirefox && css.willChange === 'filter' || isFirefox && css.filter && css.filter !== 'none') {
      return currentNode;
    } else {
      currentNode = currentNode.parentNode;
    }
  }

  return null;
}

function getOffsetParent(element) {
  var window = getWindow(element);
  var offsetParent = getTrueOffsetParent(element);

  while (offsetParent && isTableElement(offsetParent) && getComputedStyle(offsetParent).position === 'static') {
    offsetParent = getTrueOffsetParent(offsetParent);
  }

  if (offsetParent && (getNodeName(offsetParent) === 'html' || getNodeName(offsetParent) === 'body' && getComputedStyle(offsetParent).position === 'static')) {
    return window;
  }

  return offsetParent || getContainingBlock(element) || window;
}

function getMainAxisFromPlacement(placement) {
  return ['top', 'bottom'].indexOf(placement) >= 0 ? 'x' : 'y';
}

function within(min$1, value, max$1) {
  return max(min$1, min(value, max$1));
}
function withinMaxClamp(min, value, max) {
  var v = within(min, value, max);
  return v > max ? max : v;
}

function getFreshSideObject() {
  return {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0
  };
}

function mergePaddingObject(paddingObject) {
  return Object.assign({}, getFreshSideObject(), paddingObject);
}

function expandToHashMap(value, keys) {
  return keys.reduce(function (hashMap, key) {
    hashMap[key] = value;
    return hashMap;
  }, {});
}

var toPaddingObject = function toPaddingObject(padding, state) {
  padding = typeof padding === 'function' ? padding(Object.assign({}, state.rects, {
    placement: state.placement
  })) : padding;
  return mergePaddingObject(typeof padding !== 'number' ? padding : expandToHashMap(padding, basePlacements));
};

function arrow(_ref) {
  var _state$modifiersData$;

  var state = _ref.state,
      name = _ref.name,
      options = _ref.options;
  var arrowElement = state.elements.arrow;
  var popperOffsets = state.modifiersData.popperOffsets;
  var basePlacement = getBasePlacement(state.placement);
  var axis = getMainAxisFromPlacement(basePlacement);
  var isVertical = [left, right].indexOf(basePlacement) >= 0;
  var len = isVertical ? 'height' : 'width';

  if (!arrowElement || !popperOffsets) {
    return;
  }

  var paddingObject = toPaddingObject(options.padding, state);
  var arrowRect = getLayoutRect(arrowElement);
  var minProp = axis === 'y' ? top : left;
  var maxProp = axis === 'y' ? bottom : right;
  var endDiff = state.rects.reference[len] + state.rects.reference[axis] - popperOffsets[axis] - state.rects.popper[len];
  var startDiff = popperOffsets[axis] - state.rects.reference[axis];
  var arrowOffsetParent = getOffsetParent(arrowElement);
  var clientSize = arrowOffsetParent ? axis === 'y' ? arrowOffsetParent.clientHeight || 0 : arrowOffsetParent.clientWidth || 0 : 0;
  var centerToReference = endDiff / 2 - startDiff / 2;

  var min = paddingObject[minProp];
  var max = clientSize - arrowRect[len] - paddingObject[maxProp];
  var center = clientSize / 2 - arrowRect[len] / 2 + centerToReference;
  var offset = within(min, center, max);

  var axisProp = axis;
  state.modifiersData[name] = (_state$modifiersData$ = {}, _state$modifiersData$[axisProp] = offset, _state$modifiersData$.centerOffset = offset - center, _state$modifiersData$);
}

function effect$1(_ref2) {
  var state = _ref2.state,
      options = _ref2.options;
  var _options$element = options.element,
      arrowElement = _options$element === void 0 ? '[data-popper-arrow]' : _options$element;

  if (arrowElement == null) {
    return;
  }

  if (typeof arrowElement === 'string') {
    arrowElement = state.elements.popper.querySelector(arrowElement);

    if (!arrowElement) {
      return;
    }
  }

  if (!contains(state.elements.popper, arrowElement)) {

    return;
  }

  state.elements.arrow = arrowElement;
}

var arrow$1 = {
  name: 'arrow',
  enabled: true,
  phase: 'main',
  fn: arrow,
  effect: effect$1,
  requires: ['popperOffsets'],
  requiresIfExists: ['preventOverflow']
};

function getVariation(placement) {
  return placement.split('-')[1];
}

var unsetSides = {
  top: 'auto',
  right: 'auto',
  bottom: 'auto',
  left: 'auto'
};

function roundOffsetsByDPR(_ref) {
  var x = _ref.x,
      y = _ref.y;
  var win = window;
  var dpr = win.devicePixelRatio || 1;
  return {
    x: round(x * dpr) / dpr || 0,
    y: round(y * dpr) / dpr || 0
  };
}

function mapToStyles(_ref2) {
  var _Object$assign2;

  var popper = _ref2.popper,
      popperRect = _ref2.popperRect,
      placement = _ref2.placement,
      variation = _ref2.variation,
      offsets = _ref2.offsets,
      position = _ref2.position,
      gpuAcceleration = _ref2.gpuAcceleration,
      adaptive = _ref2.adaptive,
      roundOffsets = _ref2.roundOffsets,
      isFixed = _ref2.isFixed;
  var _offsets$x = offsets.x,
      x = _offsets$x === void 0 ? 0 : _offsets$x,
      _offsets$y = offsets.y,
      y = _offsets$y === void 0 ? 0 : _offsets$y;

  var _ref3 = typeof roundOffsets === 'function' ? roundOffsets({
    x: x,
    y: y
  }) : {
    x: x,
    y: y
  };

  x = _ref3.x;
  y = _ref3.y;
  var hasX = offsets.hasOwnProperty('x');
  var hasY = offsets.hasOwnProperty('y');
  var sideX = left;
  var sideY = top;
  var win = window;

  if (adaptive) {
    var offsetParent = getOffsetParent(popper);
    var heightProp = 'clientHeight';
    var widthProp = 'clientWidth';

    if (offsetParent === getWindow(popper)) {
      offsetParent = getDocumentElement(popper);

      if (getComputedStyle(offsetParent).position !== 'static' && position === 'absolute') {
        heightProp = 'scrollHeight';
        widthProp = 'scrollWidth';
      }
    }

    offsetParent = offsetParent;

    if (placement === top || (placement === left || placement === right) && variation === end) {
      sideY = bottom;
      var offsetY = isFixed && win.visualViewport ? win.visualViewport.height :
      offsetParent[heightProp];
      y -= offsetY - popperRect.height;
      y *= gpuAcceleration ? 1 : -1;
    }

    if (placement === left || (placement === top || placement === bottom) && variation === end) {
      sideX = right;
      var offsetX = isFixed && win.visualViewport ? win.visualViewport.width :
      offsetParent[widthProp];
      x -= offsetX - popperRect.width;
      x *= gpuAcceleration ? 1 : -1;
    }
  }

  var commonStyles = Object.assign({
    position: position
  }, adaptive && unsetSides);

  var _ref4 = roundOffsets === true ? roundOffsetsByDPR({
    x: x,
    y: y
  }) : {
    x: x,
    y: y
  };

  x = _ref4.x;
  y = _ref4.y;

  if (gpuAcceleration) {
    var _Object$assign;

    return Object.assign({}, commonStyles, (_Object$assign = {}, _Object$assign[sideY] = hasY ? '0' : '', _Object$assign[sideX] = hasX ? '0' : '', _Object$assign.transform = (win.devicePixelRatio || 1) <= 1 ? "translate(" + x + "px, " + y + "px)" : "translate3d(" + x + "px, " + y + "px, 0)", _Object$assign));
  }

  return Object.assign({}, commonStyles, (_Object$assign2 = {}, _Object$assign2[sideY] = hasY ? y + "px" : '', _Object$assign2[sideX] = hasX ? x + "px" : '', _Object$assign2.transform = '', _Object$assign2));
}

function computeStyles(_ref5) {
  var state = _ref5.state,
      options = _ref5.options;
  var _options$gpuAccelerat = options.gpuAcceleration,
      gpuAcceleration = _options$gpuAccelerat === void 0 ? true : _options$gpuAccelerat,
      _options$adaptive = options.adaptive,
      adaptive = _options$adaptive === void 0 ? true : _options$adaptive,
      _options$roundOffsets = options.roundOffsets,
      roundOffsets = _options$roundOffsets === void 0 ? true : _options$roundOffsets;

  var commonStyles = {
    placement: getBasePlacement(state.placement),
    variation: getVariation(state.placement),
    popper: state.elements.popper,
    popperRect: state.rects.popper,
    gpuAcceleration: gpuAcceleration,
    isFixed: state.options.strategy === 'fixed'
  };

  if (state.modifiersData.popperOffsets != null) {
    state.styles.popper = Object.assign({}, state.styles.popper, mapToStyles(Object.assign({}, commonStyles, {
      offsets: state.modifiersData.popperOffsets,
      position: state.options.strategy,
      adaptive: adaptive,
      roundOffsets: roundOffsets
    })));
  }

  if (state.modifiersData.arrow != null) {
    state.styles.arrow = Object.assign({}, state.styles.arrow, mapToStyles(Object.assign({}, commonStyles, {
      offsets: state.modifiersData.arrow,
      position: 'absolute',
      adaptive: false,
      roundOffsets: roundOffsets
    })));
  }

  state.attributes.popper = Object.assign({}, state.attributes.popper, {
    'data-popper-placement': state.placement
  });
}

var computeStyles$1 = {
  name: 'computeStyles',
  enabled: true,
  phase: 'beforeWrite',
  fn: computeStyles,
  data: {}
};

var passive = {
  passive: true
};

function effect(_ref) {
  var state = _ref.state,
      instance = _ref.instance,
      options = _ref.options;
  var _options$scroll = options.scroll,
      scroll = _options$scroll === void 0 ? true : _options$scroll,
      _options$resize = options.resize,
      resize = _options$resize === void 0 ? true : _options$resize;
  var window = getWindow(state.elements.popper);
  var scrollParents = [].concat(state.scrollParents.reference, state.scrollParents.popper);

  if (scroll) {
    scrollParents.forEach(function (scrollParent) {
      scrollParent.addEventListener('scroll', instance.update, passive);
    });
  }

  if (resize) {
    window.addEventListener('resize', instance.update, passive);
  }

  return function () {
    if (scroll) {
      scrollParents.forEach(function (scrollParent) {
        scrollParent.removeEventListener('scroll', instance.update, passive);
      });
    }

    if (resize) {
      window.removeEventListener('resize', instance.update, passive);
    }
  };
}

var eventListeners = {
  name: 'eventListeners',
  enabled: true,
  phase: 'write',
  fn: function fn() {},
  effect: effect,
  data: {}
};

var hash$1 = {
  left: 'right',
  right: 'left',
  bottom: 'top',
  top: 'bottom'
};
function getOppositePlacement(placement) {
  return placement.replace(/left|right|bottom|top/g, function (matched) {
    return hash$1[matched];
  });
}

var hash = {
  start: 'end',
  end: 'start'
};
function getOppositeVariationPlacement(placement) {
  return placement.replace(/start|end/g, function (matched) {
    return hash[matched];
  });
}

function getWindowScroll(node) {
  var win = getWindow(node);
  var scrollLeft = win.pageXOffset;
  var scrollTop = win.pageYOffset;
  return {
    scrollLeft: scrollLeft,
    scrollTop: scrollTop
  };
}

function getWindowScrollBarX(element) {

  return getBoundingClientRect(getDocumentElement(element)).left + getWindowScroll(element).scrollLeft;
}

function getViewportRect(element) {
  var win = getWindow(element);
  var html = getDocumentElement(element);
  var visualViewport = win.visualViewport;
  var width = html.clientWidth;
  var height = html.clientHeight;
  var x = 0;
  var y = 0;

  if (visualViewport) {
    width = visualViewport.width;
    height = visualViewport.height;

    if (!/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
      x = visualViewport.offsetLeft;
      y = visualViewport.offsetTop;
    }
  }

  return {
    width: width,
    height: height,
    x: x + getWindowScrollBarX(element),
    y: y
  };
}

function getDocumentRect(element) {
  var _element$ownerDocumen;

  var html = getDocumentElement(element);
  var winScroll = getWindowScroll(element);
  var body = (_element$ownerDocumen = element.ownerDocument) == null ? void 0 : _element$ownerDocumen.body;
  var width = max(html.scrollWidth, html.clientWidth, body ? body.scrollWidth : 0, body ? body.clientWidth : 0);
  var height = max(html.scrollHeight, html.clientHeight, body ? body.scrollHeight : 0, body ? body.clientHeight : 0);
  var x = -winScroll.scrollLeft + getWindowScrollBarX(element);
  var y = -winScroll.scrollTop;

  if (getComputedStyle(body || html).direction === 'rtl') {
    x += max(html.clientWidth, body ? body.clientWidth : 0) - width;
  }

  return {
    width: width,
    height: height,
    x: x,
    y: y
  };
}

function isScrollParent(element) {

  var _getComputedStyle = getComputedStyle(element),
      overflow = _getComputedStyle.overflow,
      overflowX = _getComputedStyle.overflowX,
      overflowY = _getComputedStyle.overflowY;

  return /auto|scroll|overlay|hidden/.test(overflow + overflowY + overflowX);
}

function getScrollParent(node) {
  if (['html', 'body', '#document'].indexOf(getNodeName(node)) >= 0) {

    return node.ownerDocument.body;
  }

  if (isHTMLElement(node) && isScrollParent(node)) {
    return node;
  }

  return getScrollParent(getParentNode(node));
}

function listScrollParents(element, list) {
  var _element$ownerDocumen;

  if (list === void 0) {
    list = [];
  }

  var scrollParent = getScrollParent(element);
  var isBody = scrollParent === ((_element$ownerDocumen = element.ownerDocument) == null ? void 0 : _element$ownerDocumen.body);
  var win = getWindow(scrollParent);
  var target = isBody ? [win].concat(win.visualViewport || [], isScrollParent(scrollParent) ? scrollParent : []) : scrollParent;
  var updatedList = list.concat(target);
  return isBody ? updatedList :
  updatedList.concat(listScrollParents(getParentNode(target)));
}

function rectToClientRect(rect) {
  return Object.assign({}, rect, {
    left: rect.x,
    top: rect.y,
    right: rect.x + rect.width,
    bottom: rect.y + rect.height
  });
}

function getInnerBoundingClientRect(element) {
  var rect = getBoundingClientRect(element);
  rect.top = rect.top + element.clientTop;
  rect.left = rect.left + element.clientLeft;
  rect.bottom = rect.top + element.clientHeight;
  rect.right = rect.left + element.clientWidth;
  rect.width = element.clientWidth;
  rect.height = element.clientHeight;
  rect.x = rect.left;
  rect.y = rect.top;
  return rect;
}

function getClientRectFromMixedType(element, clippingParent) {
  return clippingParent === viewport ? rectToClientRect(getViewportRect(element)) : isElement(clippingParent) ? getInnerBoundingClientRect(clippingParent) : rectToClientRect(getDocumentRect(getDocumentElement(element)));
}

function getClippingParents(element) {
  var clippingParents = listScrollParents(getParentNode(element));
  var canEscapeClipping = ['absolute', 'fixed'].indexOf(getComputedStyle(element).position) >= 0;
  var clipperElement = canEscapeClipping && isHTMLElement(element) ? getOffsetParent(element) : element;

  if (!isElement(clipperElement)) {
    return [];
  }

  return clippingParents.filter(function (clippingParent) {
    return isElement(clippingParent) && contains(clippingParent, clipperElement) && getNodeName(clippingParent) !== 'body';
  });
}

function getClippingRect(element, boundary, rootBoundary) {
  var mainClippingParents = boundary === 'clippingParents' ? getClippingParents(element) : [].concat(boundary);
  var clippingParents = [].concat(mainClippingParents, [rootBoundary]);
  var firstClippingParent = clippingParents[0];
  var clippingRect = clippingParents.reduce(function (accRect, clippingParent) {
    var rect = getClientRectFromMixedType(element, clippingParent);
    accRect.top = max(rect.top, accRect.top);
    accRect.right = min(rect.right, accRect.right);
    accRect.bottom = min(rect.bottom, accRect.bottom);
    accRect.left = max(rect.left, accRect.left);
    return accRect;
  }, getClientRectFromMixedType(element, firstClippingParent));
  clippingRect.width = clippingRect.right - clippingRect.left;
  clippingRect.height = clippingRect.bottom - clippingRect.top;
  clippingRect.x = clippingRect.left;
  clippingRect.y = clippingRect.top;
  return clippingRect;
}

function computeOffsets(_ref) {
  var reference = _ref.reference,
      element = _ref.element,
      placement = _ref.placement;
  var basePlacement = placement ? getBasePlacement(placement) : null;
  var variation = placement ? getVariation(placement) : null;
  var commonX = reference.x + reference.width / 2 - element.width / 2;
  var commonY = reference.y + reference.height / 2 - element.height / 2;
  var offsets;

  switch (basePlacement) {
    case top:
      offsets = {
        x: commonX,
        y: reference.y - element.height
      };
      break;

    case bottom:
      offsets = {
        x: commonX,
        y: reference.y + reference.height
      };
      break;

    case right:
      offsets = {
        x: reference.x + reference.width,
        y: commonY
      };
      break;

    case left:
      offsets = {
        x: reference.x - element.width,
        y: commonY
      };
      break;

    default:
      offsets = {
        x: reference.x,
        y: reference.y
      };
  }

  var mainAxis = basePlacement ? getMainAxisFromPlacement(basePlacement) : null;

  if (mainAxis != null) {
    var len = mainAxis === 'y' ? 'height' : 'width';

    switch (variation) {
      case start:
        offsets[mainAxis] = offsets[mainAxis] - (reference[len] / 2 - element[len] / 2);
        break;

      case end:
        offsets[mainAxis] = offsets[mainAxis] + (reference[len] / 2 - element[len] / 2);
        break;
    }
  }

  return offsets;
}

function detectOverflow(state, options) {
  if (options === void 0) {
    options = {};
  }

  var _options = options,
      _options$placement = _options.placement,
      placement = _options$placement === void 0 ? state.placement : _options$placement,
      _options$boundary = _options.boundary,
      boundary = _options$boundary === void 0 ? clippingParents : _options$boundary,
      _options$rootBoundary = _options.rootBoundary,
      rootBoundary = _options$rootBoundary === void 0 ? viewport : _options$rootBoundary,
      _options$elementConte = _options.elementContext,
      elementContext = _options$elementConte === void 0 ? popper : _options$elementConte,
      _options$altBoundary = _options.altBoundary,
      altBoundary = _options$altBoundary === void 0 ? false : _options$altBoundary,
      _options$padding = _options.padding,
      padding = _options$padding === void 0 ? 0 : _options$padding;
  var paddingObject = mergePaddingObject(typeof padding !== 'number' ? padding : expandToHashMap(padding, basePlacements));
  var altContext = elementContext === popper ? reference : popper;
  var popperRect = state.rects.popper;
  var element = state.elements[altBoundary ? altContext : elementContext];
  var clippingClientRect = getClippingRect(isElement(element) ? element : element.contextElement || getDocumentElement(state.elements.popper), boundary, rootBoundary);
  var referenceClientRect = getBoundingClientRect(state.elements.reference);
  var popperOffsets = computeOffsets({
    reference: referenceClientRect,
    element: popperRect,
    strategy: 'absolute',
    placement: placement
  });
  var popperClientRect = rectToClientRect(Object.assign({}, popperRect, popperOffsets));
  var elementClientRect = elementContext === popper ? popperClientRect : referenceClientRect;

  var overflowOffsets = {
    top: clippingClientRect.top - elementClientRect.top + paddingObject.top,
    bottom: elementClientRect.bottom - clippingClientRect.bottom + paddingObject.bottom,
    left: clippingClientRect.left - elementClientRect.left + paddingObject.left,
    right: elementClientRect.right - clippingClientRect.right + paddingObject.right
  };
  var offsetData = state.modifiersData.offset;

  if (elementContext === popper && offsetData) {
    var offset = offsetData[placement];
    Object.keys(overflowOffsets).forEach(function (key) {
      var multiply = [right, bottom].indexOf(key) >= 0 ? 1 : -1;
      var axis = [top, bottom].indexOf(key) >= 0 ? 'y' : 'x';
      overflowOffsets[key] += offset[axis] * multiply;
    });
  }

  return overflowOffsets;
}

function computeAutoPlacement(state, options) {
  if (options === void 0) {
    options = {};
  }

  var _options = options,
      placement = _options.placement,
      boundary = _options.boundary,
      rootBoundary = _options.rootBoundary,
      padding = _options.padding,
      flipVariations = _options.flipVariations,
      _options$allowedAutoP = _options.allowedAutoPlacements,
      allowedAutoPlacements = _options$allowedAutoP === void 0 ? placements : _options$allowedAutoP;
  var variation = getVariation(placement);
  var placements$1 = variation ? flipVariations ? variationPlacements : variationPlacements.filter(function (placement) {
    return getVariation(placement) === variation;
  }) : basePlacements;
  var allowedPlacements = placements$1.filter(function (placement) {
    return allowedAutoPlacements.indexOf(placement) >= 0;
  });

  if (allowedPlacements.length === 0) {
    allowedPlacements = placements$1;
  }

  var overflows = allowedPlacements.reduce(function (acc, placement) {
    acc[placement] = detectOverflow(state, {
      placement: placement,
      boundary: boundary,
      rootBoundary: rootBoundary,
      padding: padding
    })[getBasePlacement(placement)];
    return acc;
  }, {});
  return Object.keys(overflows).sort(function (a, b) {
    return overflows[a] - overflows[b];
  });
}

function getExpandedFallbackPlacements(placement) {
  if (getBasePlacement(placement) === auto) {
    return [];
  }

  var oppositePlacement = getOppositePlacement(placement);
  return [getOppositeVariationPlacement(placement), oppositePlacement, getOppositeVariationPlacement(oppositePlacement)];
}

function flip(_ref) {
  var state = _ref.state,
      options = _ref.options,
      name = _ref.name;

  if (state.modifiersData[name]._skip) {
    return;
  }

  var _options$mainAxis = options.mainAxis,
      checkMainAxis = _options$mainAxis === void 0 ? true : _options$mainAxis,
      _options$altAxis = options.altAxis,
      checkAltAxis = _options$altAxis === void 0 ? true : _options$altAxis,
      specifiedFallbackPlacements = options.fallbackPlacements,
      padding = options.padding,
      boundary = options.boundary,
      rootBoundary = options.rootBoundary,
      altBoundary = options.altBoundary,
      _options$flipVariatio = options.flipVariations,
      flipVariations = _options$flipVariatio === void 0 ? true : _options$flipVariatio,
      allowedAutoPlacements = options.allowedAutoPlacements;
  var preferredPlacement = state.options.placement;
  var basePlacement = getBasePlacement(preferredPlacement);
  var isBasePlacement = basePlacement === preferredPlacement;
  var fallbackPlacements = specifiedFallbackPlacements || (isBasePlacement || !flipVariations ? [getOppositePlacement(preferredPlacement)] : getExpandedFallbackPlacements(preferredPlacement));
  var placements = [preferredPlacement].concat(fallbackPlacements).reduce(function (acc, placement) {
    return acc.concat(getBasePlacement(placement) === auto ? computeAutoPlacement(state, {
      placement: placement,
      boundary: boundary,
      rootBoundary: rootBoundary,
      padding: padding,
      flipVariations: flipVariations,
      allowedAutoPlacements: allowedAutoPlacements
    }) : placement);
  }, []);
  var referenceRect = state.rects.reference;
  var popperRect = state.rects.popper;
  var checksMap = new Map();
  var makeFallbackChecks = true;
  var firstFittingPlacement = placements[0];

  for (var i = 0; i < placements.length; i++) {
    var placement = placements[i];

    var _basePlacement = getBasePlacement(placement);

    var isStartVariation = getVariation(placement) === start;
    var isVertical = [top, bottom].indexOf(_basePlacement) >= 0;
    var len = isVertical ? 'width' : 'height';
    var overflow = detectOverflow(state, {
      placement: placement,
      boundary: boundary,
      rootBoundary: rootBoundary,
      altBoundary: altBoundary,
      padding: padding
    });
    var mainVariationSide = isVertical ? isStartVariation ? right : left : isStartVariation ? bottom : top;

    if (referenceRect[len] > popperRect[len]) {
      mainVariationSide = getOppositePlacement(mainVariationSide);
    }

    var altVariationSide = getOppositePlacement(mainVariationSide);
    var checks = [];

    if (checkMainAxis) {
      checks.push(overflow[_basePlacement] <= 0);
    }

    if (checkAltAxis) {
      checks.push(overflow[mainVariationSide] <= 0, overflow[altVariationSide] <= 0);
    }

    if (checks.every(function (check) {
      return check;
    })) {
      firstFittingPlacement = placement;
      makeFallbackChecks = false;
      break;
    }

    checksMap.set(placement, checks);
  }

  if (makeFallbackChecks) {

    var numberOfChecks = flipVariations ? 3 : 1;

    var _loop = function _loop(_i) {
      var fittingPlacement = placements.find(function (placement) {
        var checks = checksMap.get(placement);

        if (checks) {
          return checks.slice(0, _i).every(function (check) {
            return check;
          });
        }
      });

      if (fittingPlacement) {
        firstFittingPlacement = fittingPlacement;
        return "break";
      }
    };

    for (var _i = numberOfChecks; _i > 0; _i--) {
      var _ret = _loop(_i);

      if (_ret === "break") break;
    }
  }

  if (state.placement !== firstFittingPlacement) {
    state.modifiersData[name]._skip = true;
    state.placement = firstFittingPlacement;
    state.reset = true;
  }
}

var flip$1 = {
  name: 'flip',
  enabled: true,
  phase: 'main',
  fn: flip,
  requiresIfExists: ['offset'],
  data: {
    _skip: false
  }
};

function getSideOffsets(overflow, rect, preventedOffsets) {
  if (preventedOffsets === void 0) {
    preventedOffsets = {
      x: 0,
      y: 0
    };
  }

  return {
    top: overflow.top - rect.height - preventedOffsets.y,
    right: overflow.right - rect.width + preventedOffsets.x,
    bottom: overflow.bottom - rect.height + preventedOffsets.y,
    left: overflow.left - rect.width - preventedOffsets.x
  };
}

function isAnySideFullyClipped(overflow) {
  return [top, right, bottom, left].some(function (side) {
    return overflow[side] >= 0;
  });
}

function hide(_ref) {
  var state = _ref.state,
      name = _ref.name;
  var referenceRect = state.rects.reference;
  var popperRect = state.rects.popper;
  var preventedOffsets = state.modifiersData.preventOverflow;
  var referenceOverflow = detectOverflow(state, {
    elementContext: 'reference'
  });
  var popperAltOverflow = detectOverflow(state, {
    altBoundary: true
  });
  var referenceClippingOffsets = getSideOffsets(referenceOverflow, referenceRect);
  var popperEscapeOffsets = getSideOffsets(popperAltOverflow, popperRect, preventedOffsets);
  var isReferenceHidden = isAnySideFullyClipped(referenceClippingOffsets);
  var hasPopperEscaped = isAnySideFullyClipped(popperEscapeOffsets);
  state.modifiersData[name] = {
    referenceClippingOffsets: referenceClippingOffsets,
    popperEscapeOffsets: popperEscapeOffsets,
    isReferenceHidden: isReferenceHidden,
    hasPopperEscaped: hasPopperEscaped
  };
  state.attributes.popper = Object.assign({}, state.attributes.popper, {
    'data-popper-reference-hidden': isReferenceHidden,
    'data-popper-escaped': hasPopperEscaped
  });
}

var hide$1 = {
  name: 'hide',
  enabled: true,
  phase: 'main',
  requiresIfExists: ['preventOverflow'],
  fn: hide
};

function distanceAndSkiddingToXY(placement, rects, offset) {
  var basePlacement = getBasePlacement(placement);
  var invertDistance = [left, top].indexOf(basePlacement) >= 0 ? -1 : 1;

  var _ref = typeof offset === 'function' ? offset(Object.assign({}, rects, {
    placement: placement
  })) : offset,
      skidding = _ref[0],
      distance = _ref[1];

  skidding = skidding || 0;
  distance = (distance || 0) * invertDistance;
  return [left, right].indexOf(basePlacement) >= 0 ? {
    x: distance,
    y: skidding
  } : {
    x: skidding,
    y: distance
  };
}

function offset(_ref2) {
  var state = _ref2.state,
      options = _ref2.options,
      name = _ref2.name;
  var _options$offset = options.offset,
      offset = _options$offset === void 0 ? [0, 0] : _options$offset;
  var data = placements.reduce(function (acc, placement) {
    acc[placement] = distanceAndSkiddingToXY(placement, state.rects, offset);
    return acc;
  }, {});
  var _data$state$placement = data[state.placement],
      x = _data$state$placement.x,
      y = _data$state$placement.y;

  if (state.modifiersData.popperOffsets != null) {
    state.modifiersData.popperOffsets.x += x;
    state.modifiersData.popperOffsets.y += y;
  }

  state.modifiersData[name] = data;
}

var offset$1 = {
  name: 'offset',
  enabled: true,
  phase: 'main',
  requires: ['popperOffsets'],
  fn: offset
};

function popperOffsets(_ref) {
  var state = _ref.state,
      name = _ref.name;

  state.modifiersData[name] = computeOffsets({
    reference: state.rects.reference,
    element: state.rects.popper,
    strategy: 'absolute',
    placement: state.placement
  });
}

var popperOffsets$1 = {
  name: 'popperOffsets',
  enabled: true,
  phase: 'read',
  fn: popperOffsets,
  data: {}
};

function getAltAxis(axis) {
  return axis === 'x' ? 'y' : 'x';
}

function preventOverflow(_ref) {
  var state = _ref.state,
      options = _ref.options,
      name = _ref.name;
  var _options$mainAxis = options.mainAxis,
      checkMainAxis = _options$mainAxis === void 0 ? true : _options$mainAxis,
      _options$altAxis = options.altAxis,
      checkAltAxis = _options$altAxis === void 0 ? false : _options$altAxis,
      boundary = options.boundary,
      rootBoundary = options.rootBoundary,
      altBoundary = options.altBoundary,
      padding = options.padding,
      _options$tether = options.tether,
      tether = _options$tether === void 0 ? true : _options$tether,
      _options$tetherOffset = options.tetherOffset,
      tetherOffset = _options$tetherOffset === void 0 ? 0 : _options$tetherOffset;
  var overflow = detectOverflow(state, {
    boundary: boundary,
    rootBoundary: rootBoundary,
    padding: padding,
    altBoundary: altBoundary
  });
  var basePlacement = getBasePlacement(state.placement);
  var variation = getVariation(state.placement);
  var isBasePlacement = !variation;
  var mainAxis = getMainAxisFromPlacement(basePlacement);
  var altAxis = getAltAxis(mainAxis);
  var popperOffsets = state.modifiersData.popperOffsets;
  var referenceRect = state.rects.reference;
  var popperRect = state.rects.popper;
  var tetherOffsetValue = typeof tetherOffset === 'function' ? tetherOffset(Object.assign({}, state.rects, {
    placement: state.placement
  })) : tetherOffset;
  var normalizedTetherOffsetValue = typeof tetherOffsetValue === 'number' ? {
    mainAxis: tetherOffsetValue,
    altAxis: tetherOffsetValue
  } : Object.assign({
    mainAxis: 0,
    altAxis: 0
  }, tetherOffsetValue);
  var offsetModifierState = state.modifiersData.offset ? state.modifiersData.offset[state.placement] : null;
  var data = {
    x: 0,
    y: 0
  };

  if (!popperOffsets) {
    return;
  }

  if (checkMainAxis) {
    var _offsetModifierState$;

    var mainSide = mainAxis === 'y' ? top : left;
    var altSide = mainAxis === 'y' ? bottom : right;
    var len = mainAxis === 'y' ? 'height' : 'width';
    var offset = popperOffsets[mainAxis];
    var min$1 = offset + overflow[mainSide];
    var max$1 = offset - overflow[altSide];
    var additive = tether ? -popperRect[len] / 2 : 0;
    var minLen = variation === start ? referenceRect[len] : popperRect[len];
    var maxLen = variation === start ? -popperRect[len] : -referenceRect[len];

    var arrowElement = state.elements.arrow;
    var arrowRect = tether && arrowElement ? getLayoutRect(arrowElement) : {
      width: 0,
      height: 0
    };
    var arrowPaddingObject = state.modifiersData['arrow#persistent'] ? state.modifiersData['arrow#persistent'].padding : getFreshSideObject();
    var arrowPaddingMin = arrowPaddingObject[mainSide];
    var arrowPaddingMax = arrowPaddingObject[altSide];

    var arrowLen = within(0, referenceRect[len], arrowRect[len]);
    var minOffset = isBasePlacement ? referenceRect[len] / 2 - additive - arrowLen - arrowPaddingMin - normalizedTetherOffsetValue.mainAxis : minLen - arrowLen - arrowPaddingMin - normalizedTetherOffsetValue.mainAxis;
    var maxOffset = isBasePlacement ? -referenceRect[len] / 2 + additive + arrowLen + arrowPaddingMax + normalizedTetherOffsetValue.mainAxis : maxLen + arrowLen + arrowPaddingMax + normalizedTetherOffsetValue.mainAxis;
    var arrowOffsetParent = state.elements.arrow && getOffsetParent(state.elements.arrow);
    var clientOffset = arrowOffsetParent ? mainAxis === 'y' ? arrowOffsetParent.clientTop || 0 : arrowOffsetParent.clientLeft || 0 : 0;
    var offsetModifierValue = (_offsetModifierState$ = offsetModifierState == null ? void 0 : offsetModifierState[mainAxis]) != null ? _offsetModifierState$ : 0;
    var tetherMin = offset + minOffset - offsetModifierValue - clientOffset;
    var tetherMax = offset + maxOffset - offsetModifierValue;
    var preventedOffset = within(tether ? min(min$1, tetherMin) : min$1, offset, tether ? max(max$1, tetherMax) : max$1);
    popperOffsets[mainAxis] = preventedOffset;
    data[mainAxis] = preventedOffset - offset;
  }

  if (checkAltAxis) {
    var _offsetModifierState$2;

    var _mainSide = mainAxis === 'x' ? top : left;

    var _altSide = mainAxis === 'x' ? bottom : right;

    var _offset = popperOffsets[altAxis];

    var _len = altAxis === 'y' ? 'height' : 'width';

    var _min = _offset + overflow[_mainSide];

    var _max = _offset - overflow[_altSide];

    var isOriginSide = [top, left].indexOf(basePlacement) !== -1;

    var _offsetModifierValue = (_offsetModifierState$2 = offsetModifierState == null ? void 0 : offsetModifierState[altAxis]) != null ? _offsetModifierState$2 : 0;

    var _tetherMin = isOriginSide ? _min : _offset - referenceRect[_len] - popperRect[_len] - _offsetModifierValue + normalizedTetherOffsetValue.altAxis;

    var _tetherMax = isOriginSide ? _offset + referenceRect[_len] + popperRect[_len] - _offsetModifierValue - normalizedTetherOffsetValue.altAxis : _max;

    var _preventedOffset = tether && isOriginSide ? withinMaxClamp(_tetherMin, _offset, _tetherMax) : within(tether ? _tetherMin : _min, _offset, tether ? _tetherMax : _max);

    popperOffsets[altAxis] = _preventedOffset;
    data[altAxis] = _preventedOffset - _offset;
  }

  state.modifiersData[name] = data;
}

var preventOverflow$1 = {
  name: 'preventOverflow',
  enabled: true,
  phase: 'main',
  fn: preventOverflow,
  requiresIfExists: ['offset']
};

function getHTMLElementScroll(element) {
  return {
    scrollLeft: element.scrollLeft,
    scrollTop: element.scrollTop
  };
}

function getNodeScroll(node) {
  if (node === getWindow(node) || !isHTMLElement(node)) {
    return getWindowScroll(node);
  } else {
    return getHTMLElementScroll(node);
  }
}

function isElementScaled(element) {
  var rect = element.getBoundingClientRect();
  var scaleX = round(rect.width) / element.offsetWidth || 1;
  var scaleY = round(rect.height) / element.offsetHeight || 1;
  return scaleX !== 1 || scaleY !== 1;
}

function getCompositeRect(elementOrVirtualElement, offsetParent, isFixed) {
  if (isFixed === void 0) {
    isFixed = false;
  }

  var isOffsetParentAnElement = isHTMLElement(offsetParent);
  var offsetParentIsScaled = isHTMLElement(offsetParent) && isElementScaled(offsetParent);
  var documentElement = getDocumentElement(offsetParent);
  var rect = getBoundingClientRect(elementOrVirtualElement, offsetParentIsScaled);
  var scroll = {
    scrollLeft: 0,
    scrollTop: 0
  };
  var offsets = {
    x: 0,
    y: 0
  };

  if (isOffsetParentAnElement || !isOffsetParentAnElement && !isFixed) {
    if (getNodeName(offsetParent) !== 'body' ||
    isScrollParent(documentElement)) {
      scroll = getNodeScroll(offsetParent);
    }

    if (isHTMLElement(offsetParent)) {
      offsets = getBoundingClientRect(offsetParent, true);
      offsets.x += offsetParent.clientLeft;
      offsets.y += offsetParent.clientTop;
    } else if (documentElement) {
      offsets.x = getWindowScrollBarX(documentElement);
    }
  }

  return {
    x: rect.left + scroll.scrollLeft - offsets.x,
    y: rect.top + scroll.scrollTop - offsets.y,
    width: rect.width,
    height: rect.height
  };
}

function order(modifiers) {
  var map = new Map();
  var visited = new Set();
  var result = [];
  modifiers.forEach(function (modifier) {
    map.set(modifier.name, modifier);
  });

  function sort(modifier) {
    visited.add(modifier.name);
    var requires = [].concat(modifier.requires || [], modifier.requiresIfExists || []);
    requires.forEach(function (dep) {
      if (!visited.has(dep)) {
        var depModifier = map.get(dep);

        if (depModifier) {
          sort(depModifier);
        }
      }
    });
    result.push(modifier);
  }

  modifiers.forEach(function (modifier) {
    if (!visited.has(modifier.name)) {

      sort(modifier);
    }
  });
  return result;
}

function orderModifiers(modifiers) {

  var orderedModifiers = order(modifiers);

  return modifierPhases.reduce(function (acc, phase) {
    return acc.concat(orderedModifiers.filter(function (modifier) {
      return modifier.phase === phase;
    }));
  }, []);
}

function debounce(fn) {
  var pending;
  return function () {
    if (!pending) {
      pending = new Promise(function (resolve) {
        Promise.resolve().then(function () {
          pending = undefined;
          resolve(fn());
        });
      });
    }

    return pending;
  };
}

function mergeByName(modifiers) {
  var merged = modifiers.reduce(function (merged, current) {
    var existing = merged[current.name];
    merged[current.name] = existing ? Object.assign({}, existing, current, {
      options: Object.assign({}, existing.options, current.options),
      data: Object.assign({}, existing.data, current.data)
    }) : current;
    return merged;
  }, {});

  return Object.keys(merged).map(function (key) {
    return merged[key];
  });
}

var DEFAULT_OPTIONS = {
  placement: 'bottom',
  modifiers: [],
  strategy: 'absolute'
};

function areValidElements() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  return !args.some(function (element) {
    return !(element && typeof element.getBoundingClientRect === 'function');
  });
}

function popperGenerator(generatorOptions) {
  if (generatorOptions === void 0) {
    generatorOptions = {};
  }

  var _generatorOptions = generatorOptions,
      _generatorOptions$def = _generatorOptions.defaultModifiers,
      defaultModifiers = _generatorOptions$def === void 0 ? [] : _generatorOptions$def,
      _generatorOptions$def2 = _generatorOptions.defaultOptions,
      defaultOptions = _generatorOptions$def2 === void 0 ? DEFAULT_OPTIONS : _generatorOptions$def2;
  return function createPopper(reference, popper, options) {
    if (options === void 0) {
      options = defaultOptions;
    }

    var state = {
      placement: 'bottom',
      orderedModifiers: [],
      options: Object.assign({}, DEFAULT_OPTIONS, defaultOptions),
      modifiersData: {},
      elements: {
        reference: reference,
        popper: popper
      },
      attributes: {},
      styles: {}
    };
    var effectCleanupFns = [];
    var isDestroyed = false;
    var instance = {
      state: state,
      setOptions: function setOptions(setOptionsAction) {
        var options = typeof setOptionsAction === 'function' ? setOptionsAction(state.options) : setOptionsAction;
        cleanupModifierEffects();
        state.options = Object.assign({}, defaultOptions, state.options, options);
        state.scrollParents = {
          reference: isElement(reference) ? listScrollParents(reference) : reference.contextElement ? listScrollParents(reference.contextElement) : [],
          popper: listScrollParents(popper)
        };

        var orderedModifiers = orderModifiers(mergeByName([].concat(defaultModifiers, state.options.modifiers)));

        state.orderedModifiers = orderedModifiers.filter(function (m) {
          return m.enabled;
        });

        runModifierEffects();
        return instance.update();
      },

      forceUpdate: function forceUpdate() {
        if (isDestroyed) {
          return;
        }

        var _state$elements = state.elements,
            reference = _state$elements.reference,
            popper = _state$elements.popper;

        if (!areValidElements(reference, popper)) {

          return;
        }

        state.rects = {
          reference: getCompositeRect(reference, getOffsetParent(popper), state.options.strategy === 'fixed'),
          popper: getLayoutRect(popper)
        };

        state.reset = false;
        state.placement = state.options.placement;

        state.orderedModifiers.forEach(function (modifier) {
          return state.modifiersData[modifier.name] = Object.assign({}, modifier.data);
        });

        for (var index = 0; index < state.orderedModifiers.length; index++) {

          if (state.reset === true) {
            state.reset = false;
            index = -1;
            continue;
          }

          var _state$orderedModifie = state.orderedModifiers[index],
              fn = _state$orderedModifie.fn,
              _state$orderedModifie2 = _state$orderedModifie.options,
              _options = _state$orderedModifie2 === void 0 ? {} : _state$orderedModifie2,
              name = _state$orderedModifie.name;

          if (typeof fn === 'function') {
            state = fn({
              state: state,
              options: _options,
              name: name,
              instance: instance
            }) || state;
          }
        }
      },

      update: debounce(function () {
        return new Promise(function (resolve) {
          instance.forceUpdate();
          resolve(state);
        });
      }),
      destroy: function destroy() {
        cleanupModifierEffects();
        isDestroyed = true;
      }
    };

    if (!areValidElements(reference, popper)) {

      return instance;
    }

    instance.setOptions(options).then(function (state) {
      if (!isDestroyed && options.onFirstUpdate) {
        options.onFirstUpdate(state);
      }
    });

    function runModifierEffects() {
      state.orderedModifiers.forEach(function (_ref3) {
        var name = _ref3.name,
            _ref3$options = _ref3.options,
            options = _ref3$options === void 0 ? {} : _ref3$options,
            effect = _ref3.effect;

        if (typeof effect === 'function') {
          var cleanupFn = effect({
            state: state,
            name: name,
            instance: instance,
            options: options
          });

          var noopFn = function noopFn() {};

          effectCleanupFns.push(cleanupFn || noopFn);
        }
      });
    }

    function cleanupModifierEffects() {
      effectCleanupFns.forEach(function (fn) {
        return fn();
      });
      effectCleanupFns = [];
    }

    return instance;
  };
}
popperGenerator();

var defaultModifiers = [eventListeners, popperOffsets$1, computeStyles$1, applyStyles$1, offset$1, flip$1, preventOverflow$1, arrow$1, hide$1];
var createPopper = popperGenerator({
  defaultModifiers: defaultModifiers
});

var hasElementType = typeof Element !== 'undefined';
var hasMap = typeof Map === 'function';
var hasSet = typeof Set === 'function';
var hasArrayBuffer = typeof ArrayBuffer === 'function' && !!ArrayBuffer.isView;

function equal(a, b) {

  if (a === b) return true;

  if (a && b && typeof a == 'object' && typeof b == 'object') {
    if (a.constructor !== b.constructor) return false;

    var length, i, keys;
    if (Array.isArray(a)) {
      length = a.length;
      if (length != b.length) return false;
      for (i = length; i-- !== 0;)
        if (!equal(a[i], b[i])) return false;
      return true;
    }

    var it;
    if (hasMap && (a instanceof Map) && (b instanceof Map)) {
      if (a.size !== b.size) return false;
      it = a.entries();
      while (!(i = it.next()).done)
        if (!b.has(i.value[0])) return false;
      it = a.entries();
      while (!(i = it.next()).done)
        if (!equal(i.value[1], b.get(i.value[0]))) return false;
      return true;
    }

    if (hasSet && (a instanceof Set) && (b instanceof Set)) {
      if (a.size !== b.size) return false;
      it = a.entries();
      while (!(i = it.next()).done)
        if (!b.has(i.value[0])) return false;
      return true;
    }

    if (hasArrayBuffer && ArrayBuffer.isView(a) && ArrayBuffer.isView(b)) {
      length = a.length;
      if (length != b.length) return false;
      for (i = length; i-- !== 0;)
        if (a[i] !== b[i]) return false;
      return true;
    }

    if (a.constructor === RegExp) return a.source === b.source && a.flags === b.flags;
    if (a.valueOf !== Object.prototype.valueOf) return a.valueOf() === b.valueOf();
    if (a.toString !== Object.prototype.toString) return a.toString() === b.toString();

    keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length) return false;

    for (i = length; i-- !== 0;)
      if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false;

    if (hasElementType && a instanceof Element) return false;

    for (i = length; i-- !== 0;) {
      if ((keys[i] === '_owner' || keys[i] === '__v' || keys[i] === '__o') && a.$$typeof) {

        continue;
      }

      if (!equal(a[keys[i]], b[keys[i]])) return false;
    }

    return true;
  }

  return a !== a && b !== b;
}

var reactFastCompare = function isEqual(a, b) {
  try {
    return equal(a, b);
  } catch (error) {
    if (((error.message || '').match(/stack|recursion/i))) {

      console.warn('react-fast-compare cannot handle circular refs');
      return false;
    }

    throw error;
  }
};

var EMPTY_MODIFIERS = [];
var usePopper = function usePopper(referenceElement, popperElement, options) {
  if (options === void 0) {
    options = {};
  }

  var prevOptions = React.useRef(null);
  var optionsWithDefaults = {
    onFirstUpdate: options.onFirstUpdate,
    placement: options.placement || 'bottom',
    strategy: options.strategy || 'absolute',
    modifiers: options.modifiers || EMPTY_MODIFIERS
  };

  var _React$useState = React.useState({
    styles: {
      popper: {
        position: optionsWithDefaults.strategy,
        left: '0',
        top: '0'
      },
      arrow: {
        position: 'absolute'
      }
    },
    attributes: {}
  }),
      state = _React$useState[0],
      setState = _React$useState[1];

  var updateStateModifier = React.useMemo(function () {
    return {
      name: 'updateState',
      enabled: true,
      phase: 'write',
      fn: function fn(_ref) {
        var state = _ref.state;
        var elements = Object.keys(state.elements);
        setState({
          styles: fromEntries(elements.map(function (element) {
            return [element, state.styles[element] || {}];
          })),
          attributes: fromEntries(elements.map(function (element) {
            return [element, state.attributes[element]];
          }))
        });
      },
      requires: ['computeStyles']
    };
  }, []);
  var popperOptions = React.useMemo(function () {
    var newOptions = {
      onFirstUpdate: optionsWithDefaults.onFirstUpdate,
      placement: optionsWithDefaults.placement,
      strategy: optionsWithDefaults.strategy,
      modifiers: [].concat(optionsWithDefaults.modifiers, [updateStateModifier, {
        name: 'applyStyles',
        enabled: false
      }])
    };

    if (reactFastCompare(prevOptions.current, newOptions)) {
      return prevOptions.current || newOptions;
    } else {
      prevOptions.current = newOptions;
      return newOptions;
    }
  }, [optionsWithDefaults.onFirstUpdate, optionsWithDefaults.placement, optionsWithDefaults.strategy, optionsWithDefaults.modifiers, updateStateModifier]);
  var popperInstanceRef = React.useRef();
  useIsomorphicLayoutEffect(function () {
    if (popperInstanceRef.current) {
      popperInstanceRef.current.setOptions(popperOptions);
    }
  }, [popperOptions]);
  useIsomorphicLayoutEffect(function () {
    if (referenceElement == null || popperElement == null) {
      return;
    }

    var createPopper$1 = options.createPopper || createPopper;
    var popperInstance = createPopper$1(referenceElement, popperElement, popperOptions);
    popperInstanceRef.current = popperInstance;
    return function () {
      popperInstance.destroy();
      popperInstanceRef.current = null;
    };
  }, [referenceElement, popperElement, options.createPopper]);
  return {
    state: popperInstanceRef.current ? popperInstanceRef.current.state : null,
    styles: state.styles,
    attributes: state.attributes,
    update: popperInstanceRef.current ? popperInstanceRef.current.update : null,
    forceUpdate: popperInstanceRef.current ? popperInstanceRef.current.forceUpdate : null
  };
};

function _toConsumableArray(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } else { return Array.from(arr); } }

var hasOwnProperty = Object.hasOwnProperty,
    setPrototypeOf = Object.setPrototypeOf,
    isFrozen = Object.isFrozen,
    getPrototypeOf = Object.getPrototypeOf,
    getOwnPropertyDescriptor = Object.getOwnPropertyDescriptor;
var freeze = Object.freeze,
    seal = Object.seal,
    create = Object.create;

var _ref = typeof Reflect !== 'undefined' && Reflect,
    apply = _ref.apply,
    construct = _ref.construct;

if (!apply) {
  apply = function apply(fun, thisValue, args) {
    return fun.apply(thisValue, args);
  };
}

if (!freeze) {
  freeze = function freeze(x) {
    return x;
  };
}

if (!seal) {
  seal = function seal(x) {
    return x;
  };
}

if (!construct) {
  construct = function construct(Func, args) {
    return new (Function.prototype.bind.apply(Func, [null].concat(_toConsumableArray(args))))();
  };
}

var arrayForEach = unapply(Array.prototype.forEach);
var arrayPop = unapply(Array.prototype.pop);
var arrayPush = unapply(Array.prototype.push);

var stringToLowerCase = unapply(String.prototype.toLowerCase);
var stringMatch = unapply(String.prototype.match);
var stringReplace = unapply(String.prototype.replace);
var stringIndexOf = unapply(String.prototype.indexOf);
var stringTrim = unapply(String.prototype.trim);

var regExpTest = unapply(RegExp.prototype.test);

var typeErrorCreate = unconstruct(TypeError);

function unapply(func) {
  return function (thisArg) {
    for (var _len = arguments.length, args = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }

    return apply(func, thisArg, args);
  };
}

function unconstruct(func) {
  return function () {
    for (var _len2 = arguments.length, args = Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }

    return construct(func, args);
  };
}

function addToSet(set, array) {
  if (setPrototypeOf) {

    setPrototypeOf(set, null);
  }

  var l = array.length;
  while (l--) {
    var element = array[l];
    if (typeof element === 'string') {
      var lcElement = stringToLowerCase(element);
      if (lcElement !== element) {

        if (!isFrozen(array)) {
          array[l] = lcElement;
        }

        element = lcElement;
      }
    }

    set[element] = true;
  }

  return set;
}

function clone(object) {
  var newObject = create(null);

  var property = void 0;
  for (property in object) {
    if (apply(hasOwnProperty, object, [property])) {
      newObject[property] = object[property];
    }
  }

  return newObject;
}

function lookupGetter(object, prop) {
  while (object !== null) {
    var desc = getOwnPropertyDescriptor(object, prop);
    if (desc) {
      if (desc.get) {
        return unapply(desc.get);
      }

      if (typeof desc.value === 'function') {
        return unapply(desc.value);
      }
    }

    object = getPrototypeOf(object);
  }

  function fallbackValue(element) {
    console.warn('fallback value for', element);
    return null;
  }

  return fallbackValue;
}

var html = freeze(['a', 'abbr', 'acronym', 'address', 'area', 'article', 'aside', 'audio', 'b', 'bdi', 'bdo', 'big', 'blink', 'blockquote', 'body', 'br', 'button', 'canvas', 'caption', 'center', 'cite', 'code', 'col', 'colgroup', 'content', 'data', 'datalist', 'dd', 'decorator', 'del', 'details', 'dfn', 'dialog', 'dir', 'div', 'dl', 'dt', 'element', 'em', 'fieldset', 'figcaption', 'figure', 'font', 'footer', 'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'head', 'header', 'hgroup', 'hr', 'html', 'i', 'img', 'input', 'ins', 'kbd', 'label', 'legend', 'li', 'main', 'map', 'mark', 'marquee', 'menu', 'menuitem', 'meter', 'nav', 'nobr', 'ol', 'optgroup', 'option', 'output', 'p', 'picture', 'pre', 'progress', 'q', 'rp', 'rt', 'ruby', 's', 'samp', 'section', 'select', 'shadow', 'small', 'source', 'spacer', 'span', 'strike', 'strong', 'style', 'sub', 'summary', 'sup', 'table', 'tbody', 'td', 'template', 'textarea', 'tfoot', 'th', 'thead', 'time', 'tr', 'track', 'tt', 'u', 'ul', 'var', 'video', 'wbr']);

var svg = freeze(['svg', 'a', 'altglyph', 'altglyphdef', 'altglyphitem', 'animatecolor', 'animatemotion', 'animatetransform', 'circle', 'clippath', 'defs', 'desc', 'ellipse', 'filter', 'font', 'g', 'glyph', 'glyphref', 'hkern', 'image', 'line', 'lineargradient', 'marker', 'mask', 'metadata', 'mpath', 'path', 'pattern', 'polygon', 'polyline', 'radialgradient', 'rect', 'stop', 'style', 'switch', 'symbol', 'text', 'textpath', 'title', 'tref', 'tspan', 'view', 'vkern']);

var svgFilters = freeze(['feBlend', 'feColorMatrix', 'feComponentTransfer', 'feComposite', 'feConvolveMatrix', 'feDiffuseLighting', 'feDisplacementMap', 'feDistantLight', 'feFlood', 'feFuncA', 'feFuncB', 'feFuncG', 'feFuncR', 'feGaussianBlur', 'feImage', 'feMerge', 'feMergeNode', 'feMorphology', 'feOffset', 'fePointLight', 'feSpecularLighting', 'feSpotLight', 'feTile', 'feTurbulence']);

var svgDisallowed = freeze(['animate', 'color-profile', 'cursor', 'discard', 'fedropshadow', 'font-face', 'font-face-format', 'font-face-name', 'font-face-src', 'font-face-uri', 'foreignobject', 'hatch', 'hatchpath', 'mesh', 'meshgradient', 'meshpatch', 'meshrow', 'missing-glyph', 'script', 'set', 'solidcolor', 'unknown', 'use']);

var mathMl = freeze(['math', 'menclose', 'merror', 'mfenced', 'mfrac', 'mglyph', 'mi', 'mlabeledtr', 'mmultiscripts', 'mn', 'mo', 'mover', 'mpadded', 'mphantom', 'mroot', 'mrow', 'ms', 'mspace', 'msqrt', 'mstyle', 'msub', 'msup', 'msubsup', 'mtable', 'mtd', 'mtext', 'mtr', 'munder', 'munderover']);

var mathMlDisallowed = freeze(['maction', 'maligngroup', 'malignmark', 'mlongdiv', 'mscarries', 'mscarry', 'msgroup', 'mstack', 'msline', 'msrow', 'semantics', 'annotation', 'annotation-xml', 'mprescripts', 'none']);

var text = freeze(['#text']);

var html$1 = freeze(['accept', 'action', 'align', 'alt', 'autocapitalize', 'autocomplete', 'autopictureinpicture', 'autoplay', 'background', 'bgcolor', 'border', 'capture', 'cellpadding', 'cellspacing', 'checked', 'cite', 'class', 'clear', 'color', 'cols', 'colspan', 'controls', 'controlslist', 'coords', 'crossorigin', 'datetime', 'decoding', 'default', 'dir', 'disabled', 'disablepictureinpicture', 'disableremoteplayback', 'download', 'draggable', 'enctype', 'enterkeyhint', 'face', 'for', 'headers', 'height', 'hidden', 'high', 'href', 'hreflang', 'id', 'inputmode', 'integrity', 'ismap', 'kind', 'label', 'lang', 'list', 'loading', 'loop', 'low', 'max', 'maxlength', 'media', 'method', 'min', 'minlength', 'multiple', 'muted', 'name', 'nonce', 'noshade', 'novalidate', 'nowrap', 'open', 'optimum', 'pattern', 'placeholder', 'playsinline', 'poster', 'preload', 'pubdate', 'radiogroup', 'readonly', 'rel', 'required', 'rev', 'reversed', 'role', 'rows', 'rowspan', 'spellcheck', 'scope', 'selected', 'shape', 'size', 'sizes', 'span', 'srclang', 'start', 'src', 'srcset', 'step', 'style', 'summary', 'tabindex', 'title', 'translate', 'type', 'usemap', 'valign', 'value', 'width', 'xmlns', 'slot']);

var svg$1 = freeze(['accent-height', 'accumulate', 'additive', 'alignment-baseline', 'ascent', 'attributename', 'attributetype', 'azimuth', 'basefrequency', 'baseline-shift', 'begin', 'bias', 'by', 'class', 'clip', 'clippathunits', 'clip-path', 'clip-rule', 'color', 'color-interpolation', 'color-interpolation-filters', 'color-profile', 'color-rendering', 'cx', 'cy', 'd', 'dx', 'dy', 'diffuseconstant', 'direction', 'display', 'divisor', 'dur', 'edgemode', 'elevation', 'end', 'fill', 'fill-opacity', 'fill-rule', 'filter', 'filterunits', 'flood-color', 'flood-opacity', 'font-family', 'font-size', 'font-size-adjust', 'font-stretch', 'font-style', 'font-variant', 'font-weight', 'fx', 'fy', 'g1', 'g2', 'glyph-name', 'glyphref', 'gradientunits', 'gradienttransform', 'height', 'href', 'id', 'image-rendering', 'in', 'in2', 'k', 'k1', 'k2', 'k3', 'k4', 'kerning', 'keypoints', 'keysplines', 'keytimes', 'lang', 'lengthadjust', 'letter-spacing', 'kernelmatrix', 'kernelunitlength', 'lighting-color', 'local', 'marker-end', 'marker-mid', 'marker-start', 'markerheight', 'markerunits', 'markerwidth', 'maskcontentunits', 'maskunits', 'max', 'mask', 'media', 'method', 'mode', 'min', 'name', 'numoctaves', 'offset', 'operator', 'opacity', 'order', 'orient', 'orientation', 'origin', 'overflow', 'paint-order', 'path', 'pathlength', 'patterncontentunits', 'patterntransform', 'patternunits', 'points', 'preservealpha', 'preserveaspectratio', 'primitiveunits', 'r', 'rx', 'ry', 'radius', 'refx', 'refy', 'repeatcount', 'repeatdur', 'restart', 'result', 'rotate', 'scale', 'seed', 'shape-rendering', 'specularconstant', 'specularexponent', 'spreadmethod', 'startoffset', 'stddeviation', 'stitchtiles', 'stop-color', 'stop-opacity', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke', 'stroke-width', 'style', 'surfacescale', 'systemlanguage', 'tabindex', 'targetx', 'targety', 'transform', 'transform-origin', 'text-anchor', 'text-decoration', 'text-rendering', 'textlength', 'type', 'u1', 'u2', 'unicode', 'values', 'viewbox', 'visibility', 'version', 'vert-adv-y', 'vert-origin-x', 'vert-origin-y', 'width', 'word-spacing', 'wrap', 'writing-mode', 'xchannelselector', 'ychannelselector', 'x', 'x1', 'x2', 'xmlns', 'y', 'y1', 'y2', 'z', 'zoomandpan']);

var mathMl$1 = freeze(['accent', 'accentunder', 'align', 'bevelled', 'close', 'columnsalign', 'columnlines', 'columnspan', 'denomalign', 'depth', 'dir', 'display', 'displaystyle', 'encoding', 'fence', 'frame', 'height', 'href', 'id', 'largeop', 'length', 'linethickness', 'lspace', 'lquote', 'mathbackground', 'mathcolor', 'mathsize', 'mathvariant', 'maxsize', 'minsize', 'movablelimits', 'notation', 'numalign', 'open', 'rowalign', 'rowlines', 'rowspacing', 'rowspan', 'rspace', 'rquote', 'scriptlevel', 'scriptminsize', 'scriptsizemultiplier', 'selection', 'separator', 'separators', 'stretchy', 'subscriptshift', 'supscriptshift', 'symmetric', 'voffset', 'width', 'xmlns']);

var xml = freeze(['xlink:href', 'xml:id', 'xlink:title', 'xml:space', 'xmlns:xlink']);

var MUSTACHE_EXPR = seal(/\{\{[\s\S]*|[\s\S]*\}\}/gm);
var ERB_EXPR = seal(/<%[\s\S]*|[\s\S]*%>/gm);
var DATA_ATTR = seal(/^data-[\-\w.\u00B7-\uFFFF]/);
var ARIA_ATTR = seal(/^aria-[\-\w]+$/);
var IS_ALLOWED_URI = seal(/^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
);
var IS_SCRIPT_OR_DATA = seal(/^(?:\w+script|data):/i);
var ATTR_WHITESPACE = seal(/[\u0000-\u0020\u00A0\u1680\u180E\u2000-\u2029\u205F\u3000]/g
);

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

function _toConsumableArray$1(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } else { return Array.from(arr); } }

var getGlobal = function getGlobal() {
  return typeof window === 'undefined' ? null : window;
};

/**
 * Creates a no-op policy for internal use only.
 * Don't export this function outside this module!
 * @param {?TrustedTypePolicyFactory} trustedTypes The policy factory.
 * @param {Document} document The document object (to determine policy name suffix)
 * @return {?TrustedTypePolicy} The policy created (or null, if Trusted Types
 * are not supported).
 */
var _createTrustedTypesPolicy = function _createTrustedTypesPolicy(trustedTypes, document) {
  if ((typeof trustedTypes === 'undefined' ? 'undefined' : _typeof(trustedTypes)) !== 'object' || typeof trustedTypes.createPolicy !== 'function') {
    return null;
  }

  var suffix = null;
  var ATTR_NAME = 'data-tt-policy-suffix';
  if (document.currentScript && document.currentScript.hasAttribute(ATTR_NAME)) {
    suffix = document.currentScript.getAttribute(ATTR_NAME);
  }

  var policyName = 'dompurify' + (suffix ? '#' + suffix : '');

  try {
    return trustedTypes.createPolicy(policyName, {
      createHTML: function createHTML(html$$1) {
        return html$$1;
      }
    });
  } catch (_) {

    console.warn('TrustedTypes policy ' + policyName + ' could not be created.');
    return null;
  }
};

function createDOMPurify() {
  var window = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : getGlobal();

  var DOMPurify = function DOMPurify(root) {
    return createDOMPurify(root);
  };

  DOMPurify.version = '2.3.5';

  DOMPurify.removed = [];

  if (!window || !window.document || window.document.nodeType !== 9) {

    DOMPurify.isSupported = false;

    return DOMPurify;
  }

  var originalDocument = window.document;

  var document = window.document;
  var DocumentFragment = window.DocumentFragment,
      HTMLTemplateElement = window.HTMLTemplateElement,
      Node = window.Node,
      Element = window.Element,
      NodeFilter = window.NodeFilter,
      _window$NamedNodeMap = window.NamedNodeMap,
      NamedNodeMap = _window$NamedNodeMap === undefined ? window.NamedNodeMap || window.MozNamedAttrMap : _window$NamedNodeMap,
      HTMLFormElement = window.HTMLFormElement,
      DOMParser = window.DOMParser,
      trustedTypes = window.trustedTypes;

  var ElementPrototype = Element.prototype;

  var cloneNode = lookupGetter(ElementPrototype, 'cloneNode');
  var getNextSibling = lookupGetter(ElementPrototype, 'nextSibling');
  var getChildNodes = lookupGetter(ElementPrototype, 'childNodes');
  var getParentNode = lookupGetter(ElementPrototype, 'parentNode');

  if (typeof HTMLTemplateElement === 'function') {
    var template = document.createElement('template');
    if (template.content && template.content.ownerDocument) {
      document = template.content.ownerDocument;
    }
  }

  var trustedTypesPolicy = _createTrustedTypesPolicy(trustedTypes, originalDocument);
  var emptyHTML = trustedTypesPolicy ? trustedTypesPolicy.createHTML('') : '';

  var _document = document,
      implementation = _document.implementation,
      createNodeIterator = _document.createNodeIterator,
      createDocumentFragment = _document.createDocumentFragment,
      getElementsByTagName = _document.getElementsByTagName;
  var importNode = originalDocument.importNode;

  var documentMode = {};
  try {
    documentMode = clone(document).documentMode ? document.documentMode : {};
  } catch (_) {}

  var hooks = {};

  DOMPurify.isSupported = typeof getParentNode === 'function' && implementation && typeof implementation.createHTMLDocument !== 'undefined' && documentMode !== 9;

  var MUSTACHE_EXPR$$1 = MUSTACHE_EXPR,
      ERB_EXPR$$1 = ERB_EXPR,
      DATA_ATTR$$1 = DATA_ATTR,
      ARIA_ATTR$$1 = ARIA_ATTR,
      IS_SCRIPT_OR_DATA$$1 = IS_SCRIPT_OR_DATA,
      ATTR_WHITESPACE$$1 = ATTR_WHITESPACE;
  var IS_ALLOWED_URI$$1 = IS_ALLOWED_URI;

  var ALLOWED_TAGS = null;
  var DEFAULT_ALLOWED_TAGS = addToSet({}, [].concat(_toConsumableArray$1(html), _toConsumableArray$1(svg), _toConsumableArray$1(svgFilters), _toConsumableArray$1(mathMl), _toConsumableArray$1(text)));

  var ALLOWED_ATTR = null;
  var DEFAULT_ALLOWED_ATTR = addToSet({}, [].concat(_toConsumableArray$1(html$1), _toConsumableArray$1(svg$1), _toConsumableArray$1(mathMl$1), _toConsumableArray$1(xml)));

  var CUSTOM_ELEMENT_HANDLING = Object.seal(Object.create(null, {
    tagNameCheck: {
      writable: true,
      configurable: false,
      enumerable: true,
      value: null
    },
    attributeNameCheck: {
      writable: true,
      configurable: false,
      enumerable: true,
      value: null
    },
    allowCustomizedBuiltInElements: {
      writable: true,
      configurable: false,
      enumerable: true,
      value: false
    }
  }));

  var FORBID_TAGS = null;

  var FORBID_ATTR = null;

  var ALLOW_ARIA_ATTR = true;

  var ALLOW_DATA_ATTR = true;

  var ALLOW_UNKNOWN_PROTOCOLS = false;

  var SAFE_FOR_TEMPLATES = false;

  var WHOLE_DOCUMENT = false;

  var SET_CONFIG = false;

  var FORCE_BODY = false;

  var RETURN_DOM = false;

  var RETURN_DOM_FRAGMENT = false;

  var RETURN_TRUSTED_TYPE = false;

  var SANITIZE_DOM = true;

  var KEEP_CONTENT = true;

  var IN_PLACE = false;

  var USE_PROFILES = {};

  var FORBID_CONTENTS = null;
  var DEFAULT_FORBID_CONTENTS = addToSet({}, ['annotation-xml', 'audio', 'colgroup', 'desc', 'foreignobject', 'head', 'iframe', 'math', 'mi', 'mn', 'mo', 'ms', 'mtext', 'noembed', 'noframes', 'noscript', 'plaintext', 'script', 'style', 'svg', 'template', 'thead', 'title', 'video', 'xmp']);

  var DATA_URI_TAGS = null;
  var DEFAULT_DATA_URI_TAGS = addToSet({}, ['audio', 'video', 'img', 'source', 'image', 'track']);

  var URI_SAFE_ATTRIBUTES = null;
  var DEFAULT_URI_SAFE_ATTRIBUTES = addToSet({}, ['alt', 'class', 'for', 'id', 'label', 'name', 'pattern', 'placeholder', 'role', 'summary', 'title', 'value', 'style', 'xmlns']);

  var MATHML_NAMESPACE = 'http://www.w3.org/1998/Math/MathML';
  var SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
  var HTML_NAMESPACE = 'http://www.w3.org/1999/xhtml';

  var NAMESPACE = HTML_NAMESPACE;
  var IS_EMPTY_INPUT = false;

  var PARSER_MEDIA_TYPE = void 0;
  var SUPPORTED_PARSER_MEDIA_TYPES = ['application/xhtml+xml', 'text/html'];
  var DEFAULT_PARSER_MEDIA_TYPE = 'text/html';
  var transformCaseFunc = void 0;

  var CONFIG = null;

  var formElement = document.createElement('form');

  var isRegexOrFunction = function isRegexOrFunction(testValue) {
    return testValue instanceof RegExp || testValue instanceof Function;
  };

  /**
   * _parseConfig
   *
   * @param  {Object} cfg optional config literal
   */

  var _parseConfig = function _parseConfig(cfg) {
    if (CONFIG && CONFIG === cfg) {
      return;
    }

    if (!cfg || (typeof cfg === 'undefined' ? 'undefined' : _typeof(cfg)) !== 'object') {
      cfg = {};
    }

    cfg = clone(cfg);

    ALLOWED_TAGS = 'ALLOWED_TAGS' in cfg ? addToSet({}, cfg.ALLOWED_TAGS) : DEFAULT_ALLOWED_TAGS;
    ALLOWED_ATTR = 'ALLOWED_ATTR' in cfg ? addToSet({}, cfg.ALLOWED_ATTR) : DEFAULT_ALLOWED_ATTR;
    URI_SAFE_ATTRIBUTES = 'ADD_URI_SAFE_ATTR' in cfg ? addToSet(clone(DEFAULT_URI_SAFE_ATTRIBUTES), cfg.ADD_URI_SAFE_ATTR) : DEFAULT_URI_SAFE_ATTRIBUTES;
    DATA_URI_TAGS = 'ADD_DATA_URI_TAGS' in cfg ? addToSet(clone(DEFAULT_DATA_URI_TAGS), cfg.ADD_DATA_URI_TAGS) : DEFAULT_DATA_URI_TAGS;
    FORBID_CONTENTS = 'FORBID_CONTENTS' in cfg ? addToSet({}, cfg.FORBID_CONTENTS) : DEFAULT_FORBID_CONTENTS;
    FORBID_TAGS = 'FORBID_TAGS' in cfg ? addToSet({}, cfg.FORBID_TAGS) : {};
    FORBID_ATTR = 'FORBID_ATTR' in cfg ? addToSet({}, cfg.FORBID_ATTR) : {};
    USE_PROFILES = 'USE_PROFILES' in cfg ? cfg.USE_PROFILES : false;
    ALLOW_ARIA_ATTR = cfg.ALLOW_ARIA_ATTR !== false;
    ALLOW_DATA_ATTR = cfg.ALLOW_DATA_ATTR !== false;
    ALLOW_UNKNOWN_PROTOCOLS = cfg.ALLOW_UNKNOWN_PROTOCOLS || false;
    SAFE_FOR_TEMPLATES = cfg.SAFE_FOR_TEMPLATES || false;
    WHOLE_DOCUMENT = cfg.WHOLE_DOCUMENT || false;
    RETURN_DOM = cfg.RETURN_DOM || false;
    RETURN_DOM_FRAGMENT = cfg.RETURN_DOM_FRAGMENT || false;
    RETURN_TRUSTED_TYPE = cfg.RETURN_TRUSTED_TYPE || false;
    FORCE_BODY = cfg.FORCE_BODY || false;
    SANITIZE_DOM = cfg.SANITIZE_DOM !== false;
    KEEP_CONTENT = cfg.KEEP_CONTENT !== false;
    IN_PLACE = cfg.IN_PLACE || false;
    IS_ALLOWED_URI$$1 = cfg.ALLOWED_URI_REGEXP || IS_ALLOWED_URI$$1;
    NAMESPACE = cfg.NAMESPACE || HTML_NAMESPACE;
    if (cfg.CUSTOM_ELEMENT_HANDLING && isRegexOrFunction(cfg.CUSTOM_ELEMENT_HANDLING.tagNameCheck)) {
      CUSTOM_ELEMENT_HANDLING.tagNameCheck = cfg.CUSTOM_ELEMENT_HANDLING.tagNameCheck;
    }

    if (cfg.CUSTOM_ELEMENT_HANDLING && isRegexOrFunction(cfg.CUSTOM_ELEMENT_HANDLING.attributeNameCheck)) {
      CUSTOM_ELEMENT_HANDLING.attributeNameCheck = cfg.CUSTOM_ELEMENT_HANDLING.attributeNameCheck;
    }

    if (cfg.CUSTOM_ELEMENT_HANDLING && typeof cfg.CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements === 'boolean') {
      CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements = cfg.CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements;
    }

    PARSER_MEDIA_TYPE =

    SUPPORTED_PARSER_MEDIA_TYPES.indexOf(cfg.PARSER_MEDIA_TYPE) === -1 ? PARSER_MEDIA_TYPE = DEFAULT_PARSER_MEDIA_TYPE : PARSER_MEDIA_TYPE = cfg.PARSER_MEDIA_TYPE;

    transformCaseFunc = PARSER_MEDIA_TYPE === 'application/xhtml+xml' ? function (x) {
      return x;
    } : stringToLowerCase;

    if (SAFE_FOR_TEMPLATES) {
      ALLOW_DATA_ATTR = false;
    }

    if (RETURN_DOM_FRAGMENT) {
      RETURN_DOM = true;
    }

    if (USE_PROFILES) {
      ALLOWED_TAGS = addToSet({}, [].concat(_toConsumableArray$1(text)));
      ALLOWED_ATTR = [];
      if (USE_PROFILES.html === true) {
        addToSet(ALLOWED_TAGS, html);
        addToSet(ALLOWED_ATTR, html$1);
      }

      if (USE_PROFILES.svg === true) {
        addToSet(ALLOWED_TAGS, svg);
        addToSet(ALLOWED_ATTR, svg$1);
        addToSet(ALLOWED_ATTR, xml);
      }

      if (USE_PROFILES.svgFilters === true) {
        addToSet(ALLOWED_TAGS, svgFilters);
        addToSet(ALLOWED_ATTR, svg$1);
        addToSet(ALLOWED_ATTR, xml);
      }

      if (USE_PROFILES.mathMl === true) {
        addToSet(ALLOWED_TAGS, mathMl);
        addToSet(ALLOWED_ATTR, mathMl$1);
        addToSet(ALLOWED_ATTR, xml);
      }
    }

    if (cfg.ADD_TAGS) {
      if (ALLOWED_TAGS === DEFAULT_ALLOWED_TAGS) {
        ALLOWED_TAGS = clone(ALLOWED_TAGS);
      }

      addToSet(ALLOWED_TAGS, cfg.ADD_TAGS);
    }

    if (cfg.ADD_ATTR) {
      if (ALLOWED_ATTR === DEFAULT_ALLOWED_ATTR) {
        ALLOWED_ATTR = clone(ALLOWED_ATTR);
      }

      addToSet(ALLOWED_ATTR, cfg.ADD_ATTR);
    }

    if (cfg.ADD_URI_SAFE_ATTR) {
      addToSet(URI_SAFE_ATTRIBUTES, cfg.ADD_URI_SAFE_ATTR);
    }

    if (cfg.FORBID_CONTENTS) {
      if (FORBID_CONTENTS === DEFAULT_FORBID_CONTENTS) {
        FORBID_CONTENTS = clone(FORBID_CONTENTS);
      }

      addToSet(FORBID_CONTENTS, cfg.FORBID_CONTENTS);
    }

    if (KEEP_CONTENT) {
      ALLOWED_TAGS['#text'] = true;
    }

    if (WHOLE_DOCUMENT) {
      addToSet(ALLOWED_TAGS, ['html', 'head', 'body']);
    }

    if (ALLOWED_TAGS.table) {
      addToSet(ALLOWED_TAGS, ['tbody']);
      delete FORBID_TAGS.tbody;
    }

    if (freeze) {
      freeze(cfg);
    }

    CONFIG = cfg;
  };

  var MATHML_TEXT_INTEGRATION_POINTS = addToSet({}, ['mi', 'mo', 'mn', 'ms', 'mtext']);

  var HTML_INTEGRATION_POINTS = addToSet({}, ['foreignobject', 'desc', 'title', 'annotation-xml']);

  var ALL_SVG_TAGS = addToSet({}, svg);
  addToSet(ALL_SVG_TAGS, svgFilters);
  addToSet(ALL_SVG_TAGS, svgDisallowed);

  var ALL_MATHML_TAGS = addToSet({}, mathMl);
  addToSet(ALL_MATHML_TAGS, mathMlDisallowed);

  /**
   *
   *
   * @param  {Element} element a DOM element whose namespace is being checked
   * @returns {boolean} Return false if the element has a
   *  namespace that a spec-compliant parser would never
   *  return. Return true otherwise.
   */
  var _checkValidNamespace = function _checkValidNamespace(element) {
    var parent = getParentNode(element);

    if (!parent || !parent.tagName) {
      parent = {
        namespaceURI: HTML_NAMESPACE,
        tagName: 'template'
      };
    }

    var tagName = stringToLowerCase(element.tagName);
    var parentTagName = stringToLowerCase(parent.tagName);

    if (element.namespaceURI === SVG_NAMESPACE) {

      if (parent.namespaceURI === HTML_NAMESPACE) {
        return tagName === 'svg';
      }

      if (parent.namespaceURI === MATHML_NAMESPACE) {
        return tagName === 'svg' && (parentTagName === 'annotation-xml' || MATHML_TEXT_INTEGRATION_POINTS[parentTagName]);
      }

      return Boolean(ALL_SVG_TAGS[tagName]);
    }

    if (element.namespaceURI === MATHML_NAMESPACE) {

      if (parent.namespaceURI === HTML_NAMESPACE) {
        return tagName === 'math';
      }

      if (parent.namespaceURI === SVG_NAMESPACE) {
        return tagName === 'math' && HTML_INTEGRATION_POINTS[parentTagName];
      }

      return Boolean(ALL_MATHML_TAGS[tagName]);
    }

    if (element.namespaceURI === HTML_NAMESPACE) {

      if (parent.namespaceURI === SVG_NAMESPACE && !HTML_INTEGRATION_POINTS[parentTagName]) {
        return false;
      }

      if (parent.namespaceURI === MATHML_NAMESPACE && !MATHML_TEXT_INTEGRATION_POINTS[parentTagName]) {
        return false;
      }

      var commonSvgAndHTMLElements = addToSet({}, ['title', 'style', 'font', 'a', 'script']);

      return !ALL_MATHML_TAGS[tagName] && (commonSvgAndHTMLElements[tagName] || !ALL_SVG_TAGS[tagName]);
    }

    return false;
  };

  /**
   * _forceRemove
   *
   * @param  {Node} node a DOM node
   */
  var _forceRemove = function _forceRemove(node) {
    arrayPush(DOMPurify.removed, { element: node });
    try {

      node.parentNode.removeChild(node);
    } catch (_) {
      try {
        node.outerHTML = emptyHTML;
      } catch (_) {
        node.remove();
      }
    }
  };

  /**
   * _removeAttribute
   *
   * @param  {String} name an Attribute name
   * @param  {Node} node a DOM node
   */
  var _removeAttribute = function _removeAttribute(name, node) {
    try {
      arrayPush(DOMPurify.removed, {
        attribute: node.getAttributeNode(name),
        from: node
      });
    } catch (_) {
      arrayPush(DOMPurify.removed, {
        attribute: null,
        from: node
      });
    }

    node.removeAttribute(name);

    if (name === 'is' && !ALLOWED_ATTR[name]) {
      if (RETURN_DOM || RETURN_DOM_FRAGMENT) {
        try {
          _forceRemove(node);
        } catch (_) {}
      } else {
        try {
          node.setAttribute(name, '');
        } catch (_) {}
      }
    }
  };

  /**
   * _initDocument
   *
   * @param  {String} dirty a string of dirty markup
   * @return {Document} a DOM, filled with the dirty markup
   */
  var _initDocument = function _initDocument(dirty) {

    var doc = void 0;
    var leadingWhitespace = void 0;

    if (FORCE_BODY) {
      dirty = '<remove></remove>' + dirty;
    } else {

      var matches = stringMatch(dirty, /^[\r\n\t ]+/);
      leadingWhitespace = matches && matches[0];
    }

    if (PARSER_MEDIA_TYPE === 'application/xhtml+xml') {

      dirty = '<html xmlns="http://www.w3.org/1999/xhtml"><head></head><body>' + dirty + '</body></html>';
    }

    var dirtyPayload = trustedTypesPolicy ? trustedTypesPolicy.createHTML(dirty) : dirty;

    if (NAMESPACE === HTML_NAMESPACE) {
      try {
        doc = new DOMParser().parseFromString(dirtyPayload, PARSER_MEDIA_TYPE);
      } catch (_) {}
    }

    if (!doc || !doc.documentElement) {
      doc = implementation.createDocument(NAMESPACE, 'template', null);
      try {
        doc.documentElement.innerHTML = IS_EMPTY_INPUT ? '' : dirtyPayload;
      } catch (_) {

      }
    }

    var body = doc.body || doc.documentElement;

    if (dirty && leadingWhitespace) {
      body.insertBefore(document.createTextNode(leadingWhitespace), body.childNodes[0] || null);
    }

    if (NAMESPACE === HTML_NAMESPACE) {
      return getElementsByTagName.call(doc, WHOLE_DOCUMENT ? 'html' : 'body')[0];
    }

    return WHOLE_DOCUMENT ? doc.documentElement : body;
  };

  /**
   * _createIterator
   *
   * @param  {Document} root document/fragment to create iterator for
   * @return {Iterator} iterator instance
   */
  var _createIterator = function _createIterator(root) {
    return createNodeIterator.call(root.ownerDocument || root, root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_COMMENT | NodeFilter.SHOW_TEXT, null, false);
  };

  /**
   * _isClobbered
   *
   * @param  {Node} elm element to check for clobbering attacks
   * @return {Boolean} true if clobbered, false if safe
   */
  var _isClobbered = function _isClobbered(elm) {
    return elm instanceof HTMLFormElement && (typeof elm.nodeName !== 'string' || typeof elm.textContent !== 'string' || typeof elm.removeChild !== 'function' || !(elm.attributes instanceof NamedNodeMap) || typeof elm.removeAttribute !== 'function' || typeof elm.setAttribute !== 'function' || typeof elm.namespaceURI !== 'string' || typeof elm.insertBefore !== 'function');
  };

  /**
   * _isNode
   *
   * @param  {Node} obj object to check whether it's a DOM node
   * @return {Boolean} true is object is a DOM node
   */
  var _isNode = function _isNode(object) {
    return (typeof Node === 'undefined' ? 'undefined' : _typeof(Node)) === 'object' ? object instanceof Node : object && (typeof object === 'undefined' ? 'undefined' : _typeof(object)) === 'object' && typeof object.nodeType === 'number' && typeof object.nodeName === 'string';
  };

  /**
   * _executeHook
   * Execute user configurable hooks
   *
   * @param  {String} entryPoint  Name of the hook's entry point
   * @param  {Node} currentNode node to work on with the hook
   * @param  {Object} data additional hook parameters
   */
  var _executeHook = function _executeHook(entryPoint, currentNode, data) {
    if (!hooks[entryPoint]) {
      return;
    }

    arrayForEach(hooks[entryPoint], function (hook) {
      hook.call(DOMPurify, currentNode, data, CONFIG);
    });
  };

  /**
   * _sanitizeElements
   *
   * @protect nodeName
   * @protect textContent
   * @protect removeChild
   *
   * @param   {Node} currentNode to check for permission to exist
   * @return  {Boolean} true if node was killed, false if left alive
   */
  var _sanitizeElements = function _sanitizeElements(currentNode) {
    var content = void 0;

    _executeHook('beforeSanitizeElements', currentNode, null);

    if (_isClobbered(currentNode)) {
      _forceRemove(currentNode);
      return true;
    }

    if (stringMatch(currentNode.nodeName, /[\u0080-\uFFFF]/)) {
      _forceRemove(currentNode);
      return true;
    }

    var tagName = transformCaseFunc(currentNode.nodeName);

    _executeHook('uponSanitizeElement', currentNode, {
      tagName: tagName,
      allowedTags: ALLOWED_TAGS
    });

    if (!_isNode(currentNode.firstElementChild) && (!_isNode(currentNode.content) || !_isNode(currentNode.content.firstElementChild)) && regExpTest(/<[/\w]/g, currentNode.innerHTML) && regExpTest(/<[/\w]/g, currentNode.textContent)) {
      _forceRemove(currentNode);
      return true;
    }

    if (tagName === 'select' && regExpTest(/<template/i, currentNode.innerHTML)) {
      _forceRemove(currentNode);
      return true;
    }

    if (!ALLOWED_TAGS[tagName] || FORBID_TAGS[tagName]) {

      if (KEEP_CONTENT && !FORBID_CONTENTS[tagName]) {
        var parentNode = getParentNode(currentNode) || currentNode.parentNode;
        var childNodes = getChildNodes(currentNode) || currentNode.childNodes;

        if (childNodes && parentNode) {
          var childCount = childNodes.length;

          for (var i = childCount - 1; i >= 0; --i) {
            parentNode.insertBefore(cloneNode(childNodes[i], true), getNextSibling(currentNode));
          }
        }
      }

      if (!FORBID_TAGS[tagName] && _basicCustomElementTest(tagName)) {
        if (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.tagNameCheck, tagName)) return false;
        if (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.tagNameCheck(tagName)) return false;
      }

      _forceRemove(currentNode);
      return true;
    }

    if (currentNode instanceof Element && !_checkValidNamespace(currentNode)) {
      _forceRemove(currentNode);
      return true;
    }

    if ((tagName === 'noscript' || tagName === 'noembed') && regExpTest(/<\/no(script|embed)/i, currentNode.innerHTML)) {
      _forceRemove(currentNode);
      return true;
    }

    if (SAFE_FOR_TEMPLATES && currentNode.nodeType === 3) {

      content = currentNode.textContent;
      content = stringReplace(content, MUSTACHE_EXPR$$1, ' ');
      content = stringReplace(content, ERB_EXPR$$1, ' ');
      if (currentNode.textContent !== content) {
        arrayPush(DOMPurify.removed, { element: currentNode.cloneNode() });
        currentNode.textContent = content;
      }
    }

    _executeHook('afterSanitizeElements', currentNode, null);

    return false;
  };

  /**
   * _isValidAttribute
   *
   * @param  {string} lcTag Lowercase tag name of containing element.
   * @param  {string} lcName Lowercase attribute name.
   * @param  {string} value Attribute value.
   * @return {Boolean} Returns true if `value` is valid, otherwise false.
   */

  var _isValidAttribute = function _isValidAttribute(lcTag, lcName, value) {

    if (SANITIZE_DOM && (lcName === 'id' || lcName === 'name') && (value in document || value in formElement)) {
      return false;
    }

    if (ALLOW_DATA_ATTR && !FORBID_ATTR[lcName] && regExpTest(DATA_ATTR$$1, lcName)) ; else if (ALLOW_ARIA_ATTR && regExpTest(ARIA_ATTR$$1, lcName)) ; else if (!ALLOWED_ATTR[lcName] || FORBID_ATTR[lcName]) {
      if (

      _basicCustomElementTest(lcTag) && (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.tagNameCheck, lcTag) || CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.tagNameCheck(lcTag)) && (CUSTOM_ELEMENT_HANDLING.attributeNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.attributeNameCheck, lcName) || CUSTOM_ELEMENT_HANDLING.attributeNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.attributeNameCheck(lcName)) ||

      lcName === 'is' && CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements && (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.tagNameCheck, value) || CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.tagNameCheck(value))) ; else {
        return false;
      }

    } else if (URI_SAFE_ATTRIBUTES[lcName]) ; else if (regExpTest(IS_ALLOWED_URI$$1, stringReplace(value, ATTR_WHITESPACE$$1, ''))) ; else if ((lcName === 'src' || lcName === 'xlink:href' || lcName === 'href') && lcTag !== 'script' && stringIndexOf(value, 'data:') === 0 && DATA_URI_TAGS[lcTag]) ; else if (ALLOW_UNKNOWN_PROTOCOLS && !regExpTest(IS_SCRIPT_OR_DATA$$1, stringReplace(value, ATTR_WHITESPACE$$1, ''))) ; else if (!value) ; else {
      return false;
    }

    return true;
  };

  /**
   * _basicCustomElementCheck
   * checks if at least one dash is included in tagName, and it's not the first char
   * for more sophisticated checking see https://github.com/sindresorhus/validate-element-name
   * @param {string} tagName name of the tag of the node to sanitize
   */
  var _basicCustomElementTest = function _basicCustomElementTest(tagName) {
    return tagName.indexOf('-') > 0;
  };

  /**
   * _sanitizeAttributes
   *
   * @protect attributes
   * @protect nodeName
   * @protect removeAttribute
   * @protect setAttribute
   *
   * @param  {Node} currentNode to sanitize
   */
  var _sanitizeAttributes = function _sanitizeAttributes(currentNode) {
    var attr = void 0;
    var value = void 0;
    var lcName = void 0;
    var l = void 0;

    _executeHook('beforeSanitizeAttributes', currentNode, null);

    var attributes = currentNode.attributes;

    if (!attributes) {
      return;
    }

    var hookEvent = {
      attrName: '',
      attrValue: '',
      keepAttr: true,
      allowedAttributes: ALLOWED_ATTR
    };
    l = attributes.length;

    while (l--) {
      attr = attributes[l];
      var _attr = attr,
          name = _attr.name,
          namespaceURI = _attr.namespaceURI;

      value = stringTrim(attr.value);
      lcName = transformCaseFunc(name);

      hookEvent.attrName = lcName;
      hookEvent.attrValue = value;
      hookEvent.keepAttr = true;
      hookEvent.forceKeepAttr = undefined;
      _executeHook('uponSanitizeAttribute', currentNode, hookEvent);
      value = hookEvent.attrValue;

      if (hookEvent.forceKeepAttr) {
        continue;
      }

      _removeAttribute(name, currentNode);

      if (!hookEvent.keepAttr) {
        continue;
      }

      if (regExpTest(/\/>/i, value)) {
        _removeAttribute(name, currentNode);
        continue;
      }

      if (SAFE_FOR_TEMPLATES) {
        value = stringReplace(value, MUSTACHE_EXPR$$1, ' ');
        value = stringReplace(value, ERB_EXPR$$1, ' ');
      }

      var lcTag = transformCaseFunc(currentNode.nodeName);
      if (!_isValidAttribute(lcTag, lcName, value)) {
        continue;
      }

      try {
        if (namespaceURI) {
          currentNode.setAttributeNS(namespaceURI, name, value);
        } else {

          currentNode.setAttribute(name, value);
        }

        arrayPop(DOMPurify.removed);
      } catch (_) {}
    }

    _executeHook('afterSanitizeAttributes', currentNode, null);
  };

  /**
   * _sanitizeShadowDOM
   *
   * @param  {DocumentFragment} fragment to iterate over recursively
   */
  var _sanitizeShadowDOM = function _sanitizeShadowDOM(fragment) {
    var shadowNode = void 0;
    var shadowIterator = _createIterator(fragment);

    _executeHook('beforeSanitizeShadowDOM', fragment, null);

    while (shadowNode = shadowIterator.nextNode()) {

      _executeHook('uponSanitizeShadowNode', shadowNode, null);

      if (_sanitizeElements(shadowNode)) {
        continue;
      }

      if (shadowNode.content instanceof DocumentFragment) {
        _sanitizeShadowDOM(shadowNode.content);
      }

      _sanitizeAttributes(shadowNode);
    }

    _executeHook('afterSanitizeShadowDOM', fragment, null);
  };

  /**
   * Sanitize
   * Public method providing core sanitation functionality
   *
   * @param {String|Node} dirty string or DOM node
   * @param {Object} configuration object
   */

  DOMPurify.sanitize = function (dirty, cfg) {
    var body = void 0;
    var importedNode = void 0;
    var currentNode = void 0;
    var oldNode = void 0;
    var returnNode = void 0;

    IS_EMPTY_INPUT = !dirty;
    if (IS_EMPTY_INPUT) {
      dirty = '<!-->';
    }

    if (typeof dirty !== 'string' && !_isNode(dirty)) {

      if (typeof dirty.toString !== 'function') {
        throw typeErrorCreate('toString is not a function');
      } else {
        dirty = dirty.toString();
        if (typeof dirty !== 'string') {
          throw typeErrorCreate('dirty is not a string, aborting');
        }
      }
    }

    if (!DOMPurify.isSupported) {
      if (_typeof(window.toStaticHTML) === 'object' || typeof window.toStaticHTML === 'function') {
        if (typeof dirty === 'string') {
          return window.toStaticHTML(dirty);
        }

        if (_isNode(dirty)) {
          return window.toStaticHTML(dirty.outerHTML);
        }
      }

      return dirty;
    }

    if (!SET_CONFIG) {
      _parseConfig(cfg);
    }

    DOMPurify.removed = [];

    if (typeof dirty === 'string') {
      IN_PLACE = false;
    }

    if (IN_PLACE) {

      if (dirty.nodeName) {
        var tagName = transformCaseFunc(dirty.nodeName);
        if (!ALLOWED_TAGS[tagName] || FORBID_TAGS[tagName]) {
          throw typeErrorCreate('root node is forbidden and cannot be sanitized in-place');
        }
      }
    } else if (dirty instanceof Node) {

      body = _initDocument('<!---->');
      importedNode = body.ownerDocument.importNode(dirty, true);
      if (importedNode.nodeType === 1 && importedNode.nodeName === 'BODY') {

        body = importedNode;
      } else if (importedNode.nodeName === 'HTML') {
        body = importedNode;
      } else {

        body.appendChild(importedNode);
      }
    } else {

      if (!RETURN_DOM && !SAFE_FOR_TEMPLATES && !WHOLE_DOCUMENT &&

      dirty.indexOf('<') === -1) {
        return trustedTypesPolicy && RETURN_TRUSTED_TYPE ? trustedTypesPolicy.createHTML(dirty) : dirty;
      }

      body = _initDocument(dirty);

      if (!body) {
        return RETURN_DOM ? null : RETURN_TRUSTED_TYPE ? emptyHTML : '';
      }
    }

    if (body && FORCE_BODY) {
      _forceRemove(body.firstChild);
    }

    var nodeIterator = _createIterator(IN_PLACE ? dirty : body);

    while (currentNode = nodeIterator.nextNode()) {

      if (currentNode.nodeType === 3 && currentNode === oldNode) {
        continue;
      }

      if (_sanitizeElements(currentNode)) {
        continue;
      }

      if (currentNode.content instanceof DocumentFragment) {
        _sanitizeShadowDOM(currentNode.content);
      }

      _sanitizeAttributes(currentNode);

      oldNode = currentNode;
    }

    oldNode = null;

    if (IN_PLACE) {
      return dirty;
    }

    if (RETURN_DOM) {
      if (RETURN_DOM_FRAGMENT) {
        returnNode = createDocumentFragment.call(body.ownerDocument);

        while (body.firstChild) {

          returnNode.appendChild(body.firstChild);
        }
      } else {
        returnNode = body;
      }

      if (ALLOWED_ATTR.shadowroot) {

        returnNode = importNode.call(originalDocument, returnNode, true);
      }

      return returnNode;
    }

    var serializedHTML = WHOLE_DOCUMENT ? body.outerHTML : body.innerHTML;

    if (SAFE_FOR_TEMPLATES) {
      serializedHTML = stringReplace(serializedHTML, MUSTACHE_EXPR$$1, ' ');
      serializedHTML = stringReplace(serializedHTML, ERB_EXPR$$1, ' ');
    }

    return trustedTypesPolicy && RETURN_TRUSTED_TYPE ? trustedTypesPolicy.createHTML(serializedHTML) : serializedHTML;
  };

  /**
   * Public method to set the configuration once
   * setConfig
   *
   * @param {Object} cfg configuration object
   */
  DOMPurify.setConfig = function (cfg) {
    _parseConfig(cfg);
    SET_CONFIG = true;
  };

  DOMPurify.clearConfig = function () {
    CONFIG = null;
    SET_CONFIG = false;
  };

  /**
   * Public method to check if an attribute value is valid.
   * Uses last set config, if any. Otherwise, uses config defaults.
   * isValidAttribute
   *
   * @param  {string} tag Tag name of containing element.
   * @param  {string} attr Attribute name.
   * @param  {string} value Attribute value.
   * @return {Boolean} Returns true if `value` is valid. Otherwise, returns false.
   */
  DOMPurify.isValidAttribute = function (tag, attr, value) {

    if (!CONFIG) {
      _parseConfig({});
    }

    var lcTag = transformCaseFunc(tag);
    var lcName = transformCaseFunc(attr);
    return _isValidAttribute(lcTag, lcName, value);
  };

  /**
   * AddHook
   * Public method to add DOMPurify hooks
   *
   * @param {String} entryPoint entry point for the hook to add
   * @param {Function} hookFunction function to execute
   */
  DOMPurify.addHook = function (entryPoint, hookFunction) {
    if (typeof hookFunction !== 'function') {
      return;
    }

    hooks[entryPoint] = hooks[entryPoint] || [];
    arrayPush(hooks[entryPoint], hookFunction);
  };

  /**
   * RemoveHook
   * Public method to remove a DOMPurify hook at a given entryPoint
   * (pops it from the stack of hooks if more are present)
   *
   * @param {String} entryPoint entry point for the hook to remove
   */
  DOMPurify.removeHook = function (entryPoint) {
    if (hooks[entryPoint]) {
      arrayPop(hooks[entryPoint]);
    }
  };

  /**
   * RemoveHooks
   * Public method to remove all DOMPurify hooks at a given entryPoint
   *
   * @param  {String} entryPoint entry point for the hooks to remove
   */
  DOMPurify.removeHooks = function (entryPoint) {
    if (hooks[entryPoint]) {
      hooks[entryPoint] = [];
    }
  };

  DOMPurify.removeAllHooks = function () {
    hooks = {};
  };

  return DOMPurify;
}

var purify = createDOMPurify();

/**
 * @param data argument that needs to be sanitized
 * @description function to generate a sanitized string
 * @function
 * @returns {string} returns a sanitized string
 */
const Harpic = (data) => `${purify.sanitize(data)}`;
/**
 *
 * @param {Object} obj `object` the object whose key you want to check
 * @param {String} prop `string` the key/parameter that you want to check if it exists
 * @returns `Boolean`
 * @description to check if an object has a key or not
 */
const hasOwn = (obj, prop) => Object.prototype.hasOwnProperty.call(obj, prop);

const checkDocument = typeof window !== 'undefined' &&
    typeof window.document !== 'undefined' &&
    typeof window.document.createElement !== 'undefined';
/**
 * @type string | null
 * @description returns the userAgent string of the browser/whatever is in use.
 * @returns {string | null} string or null
 */
const userAgent = checkDocument
    ? navigator.userAgent || navigator.vendor || window.opera
    : null;
/**
 * @type string | null
 * @description Returns the Operating system of the mobile device in use (`iOS`, `Android`, `bhindoj` for windows and false for neither)
 * @returns {string | null} string or null
 */
const getMobileOperatingSystem = checkDocument &&
    userAgent &&
    (/(iPad|iPhone|iPod)/i.test(userAgent)
        ? 'iOS'
        : /Android/i.test(userAgent)
            ? 'Android'
            : /Windows Phone/i.test(userAgent)
                ? 'bhindoj'
                : false);

checkDocument && document.body.isEqualNode ? 'isEqualNode' : 'isSameNode';
/**
 * @type Function
 * @description A function which returns scroll bar width
 * @returns {number}
 */
const getScrollbarWidth = (() => {
    let memoWidth = NaN;
    return () => {
        if (checkDocument) {
            if (!Number.isNaN(memoWidth) && memoWidth !== null) {
                return memoWidth;
            }
            const scrollDiv = document.createElement('div');
            scrollDiv.className = 'scrollbar-measure';
            document.body.appendChild(scrollDiv);

            memoWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
            document.body.removeChild(scrollDiv);
            return memoWidth;
        }
        return 0;
    };
})();
/**
 * @type Function
 * @description A function that returns which transitionEnd is available in the browser.
 */
const whichTransitionEndEvent = () => {
    if (checkDocument) {
        const el = document.createElement('fakeelement');
        const transitions = {
            transition: 'transitionend',
            OTransition: 'oTransitionEnd',
            MozTransition: 'transitionend',
            webkitTransition: 'webkitTransitionEnd',
        };
        const key = (Object.keys(transitions).find(t => el.style[t] !== undefined));
        return transitions[key];
    }
    return 'transitionend';
};
const getTransitionEndEvent = whichTransitionEndEvent();
/**
 * @type Function
 * @description A function that returns which animationEnd event needs to be executed.
 */
const whichAnimationEndEvent = () => {
    if (checkDocument) {
        const el = document.createElement('fakeelement');
        const animations = {
            animation: 'animationend',
            OAnimation: 'oAnimationEnd',
            MozAnimation: 'animationend',
            WebkitAnimation: 'webkitAnimationEnd',
        };
        const key = (Object.keys(animations).find(t => el.style[t] !== undefined));
        return animations[key];
    }
    return 'animationend';
};
const getAnimationEndEvent = whichAnimationEndEvent();
/**
 *
 * @param {Function} fn the animation function to be executed for each frame
 * @param {Number} init initial position
 * @param {Number} final final position
 * @param {Number} start position to start from
 * @param {Number} time total time the animation should take in ms
 * @param {Function} oneEnd callback function at the end of an animation
 * @param {Number} refreshRate refresh rate of the monitor (60 by default)
 * @type Function
 * @description A function that helps in executing animations.
 */
const smoothAnimate = (fn, init, final, start, time, oneEnd, refreshRate = 60) => {
    let rafVal;
    if (checkDocument && hasOwn(window, 'requestAnimationFrame')) {
        const rafFn = (curr = start) => {
            if ((final > init && curr < final) ||
                (final < init && curr > final)) {
                rafVal = requestAnimationFrame(() => {
                    fn(curr);
                    const newCurr = curr + (final - init) / (time * refreshRate);
                    rafFn(newCurr);
                });
            }
            if (typeof oneEnd === 'function' &&
                ((final > init && curr >= final) ||
                    (final < init && curr <= final))) {
                requestAnimationFrame(() => {
                    oneEnd();
                });
            }
        };
        rafFn();
    }
    else {
        fn(final);
    }
    return {
        getRafVal: () => rafVal,
    };
};

const Portalify = ({ children, id, className }) => {
    if (children && checkDocument) {
        let $id = document.getElementById(id);
        if (!$id) {
            $id = document.createElement('div');
            $id.id = id;
            if (className) {
                $id.className = className;
            }
            document.body.appendChild($id);

        }
        return createPortal(children, $id);
    }
    return null;
};

let closeTimeout = 0;
const Tooltip = ({ id = '', isOpen = undefined, onClickOpen, onClick, wrappedClass, wrappedContent, referenceElementWrapperClass, noPortal = false, ...rem }) => {
    const referenceElement = useRef(null);
    const [ttState, setTTState] = useState(false);
    const popperElement = useRef(null);
    const isIOS = useRef(getMobileOperatingSystem === 'iOS');
    const virtualReference = useMemo(() => {
        return {
            getBoundingClientRect() {
                const el = referenceElement.current;
                if (el !== null) {
                    const rect = el.getBoundingClientRect();
                    const a = {
                        top: rect.top,
                        left: rect.left,
                        width: el.offsetWidth,
                        height: el.offsetHeight,
                        right: 0,
                        middle: 0,
                        center: 0,
                        bottom: 0,
                    };
                    const offsetTop = el.offsetTop;
                    a.right = rect.right || a.left + a.width;
                    a.bottom =
                        (isIOS.current
                            ? offsetTop + a.height
                            : rect.bottom) ||
                            (isIOS.current ? offsetTop : a.top) + a.height;
                    a.middle = a.left + (a.right - a.left) / 2;
                    a.center = a.top + (a.bottom - a.top) / 2;
                    return a;
                }
                return {
                    top: 0,
                    left: 0,
                    width: 0,
                    height: 0,
                    right: 0,
                    middle: 0,
                    center: 0,
                    bottom: 0,
                };
            },
        };
    },

    [referenceElement.current]);
    useEffect(() => {
        if (isOpen !== undefined) {
            setTTState(isOpen);
        }
    }, [isOpen]);
    useEffect(() => {
        const onClickHandlerForDoc = (docEv) => {
            if ((onClickOpen && ttState) || (ttState && isIOS.current)) {
                if (!popperElement.current?.contains(docEv.target)) {
                    setTTState(false);
                }
            }
        };
        if (ttState) {
            if (onClickOpen) {
                document.addEventListener('click', onClickHandlerForDoc);
            }
            if (isIOS.current) {
                document.addEventListener('touchstart', onClickHandlerForDoc);
            }
        }
        return () => {
            document.removeEventListener('click', onClickHandlerForDoc);
            document.removeEventListener('touchstart', onClickHandlerForDoc);
        };
    }, [onClickOpen, ttState]);
    return (React__default.createElement("div", { className: wrappedClass, onMouseOver: () => {
            if (isOpen === undefined && onClickOpen === undefined) {
                clearTimeout(closeTimeout);
                setTTState(true);
            }
        }, onMouseLeave: e => {
            if (isOpen === undefined &&
                onClickOpen === undefined &&
                !popperElement.current?.contains(e.target)) {
                closeTimeout = window.setTimeout(() => {
                    setTTState(false);
                }, 200);
            }
        }, onClick: e => {
            if (onClickOpen) {
                if (ttState &&
                    !popperElement.current?.contains(e.target) &&
                    referenceElement.current?.contains(e.target)) {
                    setTTState(false);
                }
                else {
                    setTTState(true);
                }
            }
        } },
        React__default.createElement("div", { ref: referenceElement, className: referenceElementWrapperClass, onClick: e => {
                if (typeof onClick === 'function') {
                    onClick(e);
                }
            } }, wrappedContent),
        noPortal ? (React__default.createElement(Popper, { virtualReference: virtualReference, ttState: ttState, popperElement: popperElement, remainingProps: rem, isOpen: isOpen, onClickOpen: onClickOpen, setTTState: setTTState })) : (React__default.createElement(Portalify, { id: !!id ? `${id}-tooltip` : 'tooltip-container' },
            React__default.createElement(Popper, { virtualReference: virtualReference, ttState: ttState, popperElement: popperElement, remainingProps: rem, isOpen: isOpen, onClickOpen: onClickOpen, setTTState: setTTState })))));
};
const Popper = ({ virtualReference, ttState, popperElement, isOpen, onClickOpen, setTTState, remainingProps: { offset, arrow, mini, minWidth, ttClass, children, getIsOpenState, padding, position, ttWidthEqualToRefElement, }, }) => {
    const [arrowElement, setArrowElement] = useState(null);
    const { state, styles, attributes, forceUpdate } = usePopper(virtualReference, popperElement.current, {
        strategy: 'absolute',
        placement: position || 'bottom',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: offset || (arrow ? [0, 12] : [0, 6]),
                },
            },
            {
                name: 'arrow',
                options: {
                    element: arrowElement,
                    padding: 6,
                },
            },
            {
                name: 'preventOverflow',
                options: {
                    altBoundary: true,
                    padding: padding || { right: 15, left: 15 },
                },
            },
        ],
    });
    useEffect(() => {
        if (forceUpdate) {
            forceUpdate();
        }
    }, [forceUpdate]);
    useEffect(() => {
        const el = popperElement.current;
        if (el !== null) {
            if (ttState) {
                if (forceUpdate) {
                    forceUpdate();
                }
                el.classList.remove('c-toolTip--isClosing');
                el.classList.add('c-toolTip--isOpening', 'c-toolTip--visible');
            }
        }

        if (getIsOpenState !== undefined) {
            getIsOpenState(ttState);
        }
        return () => {
            if (el !== null) {
                if (ttState) {
                    el.classList.remove('c-toolTip--isOpening');
                    el.classList.add('c-toolTip--isClosing');
                    el.classList.remove('c-toolTip--visible');
                }
            }
        };
    },

    [ttState, forceUpdate]);
    let upperHiddenBoxHeight = 3;
    if (offset) {
        upperHiddenBoxHeight += offset[1];
    }
    else if (arrow) {
        upperHiddenBoxHeight += 12;
    }
    else {
        upperHiddenBoxHeight += 6;
    }
    return (React__default.createElement("div", { ref: popperElement, style: {
            ...styles.popper,
            width: ttWidthEqualToRefElement && state && state.rects
                ? `${state.rects.reference.width}px`
                : '',
        }, className: classNames('c-toolTip', {
            'c-toolTip--mini': mini,
        }, {
            'c-toolTip--minWidth': minWidth,
        }, ttClass), onMouseOver: () => {
            if (isOpen === undefined && onClickOpen === undefined) {
                clearTimeout(closeTimeout);
                setTTState(true);
            }
        }, onMouseLeave: () => {
            if (isOpen === undefined && onClickOpen === undefined) {
                setTTState(false);
            }
        }, ...attributes.popper },
        children,
        React__default.createElement("div", { style: {
                position: 'absolute',
                top: `-${upperHiddenBoxHeight}px`,
                width: '100%',
                height: `${upperHiddenBoxHeight}px`,
                background: 'transparent',
                zIndex: -1,
            } }),
        arrow && (React__default.createElement("div", { ref: setArrowElement, style: styles.arrow, className: "c-toolTip__arrow" }))));
};

const LoaderBars = ({ fill, className, width, height, ...restProps }) => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", fill: fill, className: className, width: width, height: height, viewBox: "0 0 135 140", ...restProps },
    React__default.createElement("rect", { y: "17.9753", width: "15", height: "104.049", rx: "6" },
        React__default.createElement("animate", { attributeName: "height", begin: "0.5s", dur: "1s", values: "120;110;100;90;80;70;60;50;40;140;120", calcMode: "linear", repeatCount: "indefinite" }),
        React__default.createElement("animate", { attributeName: "y", begin: "0.5s", dur: "1s", values: "10;15;20;25;30;35;40;45;50;0;10", calcMode: "linear", repeatCount: "indefinite" })),
    React__default.createElement("rect", { x: "30", y: "30.4753", width: "15", height: "79.0494", rx: "6" },
        React__default.createElement("animate", { attributeName: "height", begin: "0.25s", dur: "1s", values: "120;110;100;90;80;70;60;50;40;140;120", calcMode: "linear", repeatCount: "indefinite" }),
        React__default.createElement("animate", { attributeName: "y", begin: "0.25s", dur: "1s", values: "10;15;20;25;30;35;40;45;50;0;10", calcMode: "linear", repeatCount: "indefinite" })),
    React__default.createElement("rect", { x: "60", width: "15", height: "54.0494", rx: "6", y: "42.9753" },
        React__default.createElement("animate", { attributeName: "height", begin: "0s", dur: "1s", values: "120;110;100;90;80;70;60;50;40;140;120", calcMode: "linear", repeatCount: "indefinite" }),
        React__default.createElement("animate", { attributeName: "y", begin: "0s", dur: "1s", values: "10;15;20;25;30;35;40;45;50;0;10", calcMode: "linear", repeatCount: "indefinite" })),
    React__default.createElement("rect", { x: "90", y: "30.4753", width: "15", height: "79.0494", rx: "6" },
        React__default.createElement("animate", { attributeName: "height", begin: "0.25s", dur: "1s", values: "120;110;100;90;80;70;60;50;40;140;120", calcMode: "linear", repeatCount: "indefinite" }),
        React__default.createElement("animate", { attributeName: "y", begin: "0.25s", dur: "1s", values: "10;15;20;25;30;35;40;45;50;0;10", calcMode: "linear", repeatCount: "indefinite" })),
    React__default.createElement("rect", { x: "120", y: "17.9753", width: "15", height: "104.049", rx: "6" },
        React__default.createElement("animate", { attributeName: "height", begin: "0.5s", dur: "1s", values: "120;110;100;90;80;70;60;50;40;140;120", calcMode: "linear", repeatCount: "indefinite" }),
        React__default.createElement("animate", { attributeName: "y", begin: "0.5s", dur: "1s", values: "10;15;20;25;30;35;40;45;50;0;10", calcMode: "linear", repeatCount: "indefinite" }))));
var bars = memo(LoaderBars);

class LoaderLinearDotsPlain extends PureComponent {
    state = {
        render: '.',
    };
    renderDots = '...';
    dots = 0;
    interval = 0;
    componentDidMount() {
        this.dotsTimer(this.props.speed);
    }
    componentWillUnmount() {
        window.clearInterval(this.interval);
    }
    changeDots = (dots) => {
        let nextDotState = dots + 1;
        if (nextDotState === this.renderDots.length + 1) {
            nextDotState = 0;
        }
        return nextDotState;
    };
    dotsTimer = (speed = 1000) => {
        this.interval = window.setInterval(() => {
            this.setState({
                render: this.renderDots.slice(0, this.dots),
            });
            this.dots = this.changeDots(this.dots);
        }, speed);
    };
    render() {
        return (React__default.createElement("span", { className: this.props.className }, this.state.render));
    }
}

const LoaderLinearDots = ({ fill, className, width, height, ...restProps }) => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", fill: fill, className: className, width: width, height: height, viewBox: "0 0 120 30", ...restProps },
    React__default.createElement("circle", { cx: "15", cy: "15", r: "11.2701" },
        React__default.createElement("animate", { attributeName: "r", from: "15", to: "15", begin: "0s", dur: "0.8s", values: "15;9;15", calcMode: "linear", repeatCount: "indefinite" }),
        React__default.createElement("animate", { attributeName: "fill-opacity", from: "1", to: "1", begin: "0s", dur: "0.8s", values: "1;.5;1", calcMode: "linear", repeatCount: "indefinite" })),
    React__default.createElement("circle", { cx: "60", cy: "15", r: "12.7299", fillOpacity: "0.3" },
        React__default.createElement("animate", { attributeName: "r", from: "9", to: "9", begin: "0s", dur: "0.8s", values: "9;15;9", calcMode: "linear", repeatCount: "indefinite" }),
        React__default.createElement("animate", { attributeName: "fill-opacity", from: "0.5", to: "0.5", begin: "0s", dur: "0.8s", values: ".5;1;.5", calcMode: "linear", repeatCount: "indefinite" })),
    React__default.createElement("circle", { cx: "105", cy: "15", r: "11.2701" },
        React__default.createElement("animate", { attributeName: "r", from: "15", to: "15", begin: "0s", dur: "0.8s", values: "15;9;15", calcMode: "linear", repeatCount: "indefinite" }),
        React__default.createElement("animate", { attributeName: "fill-opacity", from: "1", to: "1", begin: "0s", dur: "0.8s", values: "1;.5;1", calcMode: "linear", repeatCount: "indefinite" }))));
var linearDots = memo(LoaderLinearDots);

const LoaderPuff = ({ stroke, className, width, height, strokeWidth, ...restProps }) => (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", stroke: stroke || 'currentColor', className: className, width: width, height: height, viewBox: "0 0 44 44", ...restProps },
    React__default.createElement("g", { fill: "none", fillRule: "evenodd", strokeWidth: "2" },
        React__default.createElement("circle", { cx: "22", cy: "22", r: "1", strokeWidth: strokeWidth },
            React__default.createElement("animate", { attributeName: "r", begin: "0s", calcMode: "spline", dur: "1.8s", keySplines: "0.165, 0.84, 0.44, 1", keyTimes: "0; 1", repeatCount: "indefinite", values: "1; 20" }),
            React__default.createElement("animate", { attributeName: "stroke-opacity", begin: "0s", calcMode: "spline", dur: "1.8s", keySplines: "0.3, 0.61, 0.355, 1", keyTimes: "0; 1", repeatCount: "indefinite", values: "1; 0" })),
        React__default.createElement("circle", { cx: "22", cy: "22", r: "1", strokeWidth: strokeWidth },
            React__default.createElement("animate", { attributeName: "r", begin: "-0.9s", calcMode: "spline", dur: "1.8s", keySplines: "0.165, 0.84, 0.44, 1", keyTimes: "0; 1", repeatCount: "indefinite", values: "1; 20" }),
            React__default.createElement("animate", { attributeName: "stroke-opacity", begin: "-0.9s", calcMode: "spline", dur: "1.8s", keySplines: "0.3, 0.61, 0.355, 1", keyTimes: "0; 1", repeatCount: "indefinite", values: "1; 0" })))));
var puff = memo(LoaderPuff);

const SmallPulse = ({ className, renderProp, ...moreProps }) => (React__default.createElement("div", { className: `bb__pulse-svg ${className}`, ...moreProps },
    React__default.createElement("div", { className: "bb__pulse-svg__circle bb__first-circle" }),
    React__default.createElement("div", { className: "bb__pulse-svg__circle bb__second-circle" }),
    React__default.createElement("div", { className: "bb__pulse-svg__circle bb__third-circle" }),
    typeof renderProp === 'function' ? renderProp() : ''));
var smallPulse = memo(SmallPulse);

const Li$1 = ({ className = '', style, children, id }) => {
    const { prefix } = useTheme();
    const prefixedText = prefix ? `${prefix}-` : '';
    return (React__default.createElement("li", { className: `${prefixedText}ol__li ${className}`, id: id, style: style }, children));
};
const Ol = ({ className = '', style, children, id }) => {
    const { prefix } = useTheme();
    const prefixedText = prefix ? `${prefix}-` : '';
    return (React__default.createElement("ol", { className: `${prefixedText}ol--circleNumbered ${className}`, id: id, style: style }, children));
};
var CircleNumbered = {
    Li: Li$1,
    Ol,
};

const Li = ({ className = '', children, id, style }) => (React__default.createElement("li", { className: `${className}`, id: id, style: style }, children));
const Ul = ({ className = '', children, id, style }) => {
    const { prefix } = useTheme();
    const prefixedText = prefix ? `${prefix}-` : '';
    return (React__default.createElement("ul", { id: id, className: `${prefixedText}list-disc ${prefixedText}list-outside ${className}`, style: style }, children));
};
var Bulleted = {
    Li,
    Ul,
};

class Modal extends Component {
    $modalMain = createRef();
    $modalWrap = createRef();
    closeModalToFocus;
    constructor(props) {
        super(props);
        this.state = {
            modalOpen: props.openMoi,
        };
        this.closeModalToFocus = createRef();
    }
    componentDidMount() {
        if (this.props.openMoi) {
            this.modalOpenerMain();
        }
    }
    componentDidUpdate(prevProps) {
        const { openMoi } = this.props;
        if (prevProps.openMoi !== openMoi) {
            if (openMoi) {
                this.modalOpenerMain();
            }
            else {
                this.modalCloserMain();
            }
        }
    }
    componentWillUnmount() {
        document.removeEventListener('keyup', this.listenToEscape);
        this.enableBodyScroll();
        this.onModalClosedNShutDown();
    }
    enableBodyScroll = () => {
        const { prefix } = this.context;
        const prefixedText = prefix ? `${prefix}-` : '';
        requestAnimationFrame(() => {
            if (checkDocument &&
                !document.querySelector(`.${prefixedText}c-modals--open`)) {
                const $body = document.body;
                $body.classList.remove(`${prefixedText}modal-shown`);

                $body.style.marginRight = '';

            }
        });
    };
    listenToEscape = (e) => {
        if (e.code === 'Escape') {
            this.closeThisModal();
        }
    };
    modalMainOpenAnimateEnd = () => {
        const $modalMain = this.$modalMain.current;
        const { prefix } = this.context;
        const prefixedText = prefix ? `${prefix}-` : '';
        $modalMain.classList.remove(`${prefixedText}c-modal__main--isOpening`);
        $modalMain.removeEventListener(getAnimationEndEvent, this.modalMainOpenAnimateEnd);
        requestAnimationFrame(() => {
            this.onModalOpenedNReady();
        });
    };
    onModalOpenedNReady = () => {
        const { footer } = this.props;
        const { onOpenNReady } = this.props;
        if (typeof onOpenNReady === 'function') {
            onOpenNReady();
        }
        if (checkDocument) {
            document.addEventListener('keyup', this.listenToEscape);
            if (Array.isArray(footer) &&
                !footer.some(btn => hasOwn(btn, 'focus')) &&
                this.closeModalToFocus.current &&
                typeof this.closeModalToFocus.current.focus === 'function') {

                this.closeModalToFocus.current.focus();

            }
        }
    };
    modalMainCloseAnimateEnd = () => {
        const $modalMain = this.$modalMain.current;
        const { prefix } = this.context;
        const prefixedText = prefix ? `${prefix}-` : '';
        requestAnimationFrame(() => {
            this.onModalClosedNShutDown();
            this.enableBodyScroll();
            $modalMain.classList.remove(`${prefixedText}c-modal__main--isClosing`);
        });
        $modalMain.removeEventListener(getAnimationEndEvent, this.modalMainCloseAnimateEnd);

    };
    onModalClosedNShutDown = () => {
        const { openMoi, onCloseNShutDown = () => { } } = this.props;
        if (!openMoi) {
            this.setState({
                modalOpen: false,
            }, onCloseNShutDown);
        }
    };
    modalWrapOpenAnimateEnd = () => {
        const $modalWrap = this.$modalWrap.current;
        const { onModalOpen } = this.props;
        const { prefix } = this.context;
        const prefixedText = prefix ? `${prefix}-` : '';
        if ($modalWrap) {
            $modalWrap.classList.remove(`${prefixedText}c-modals--isOpening`);
            $modalWrap.removeEventListener(getAnimationEndEvent, this.modalWrapOpenAnimateEnd);
        }

        if (typeof onModalOpen === 'function') {
            onModalOpen();
        }
    };
    modalWrapCloseAnimateEnd = () => {
        const $modalWrap = this.$modalWrap.current;
        const { prefix } = this.context;
        const prefixedText = prefix ? `${prefix}-` : '';
        if ($modalWrap) {
            $modalWrap.classList.remove(`${prefixedText}c-modals--isClosing`);
            $modalWrap.removeEventListener(getAnimationEndEvent, this.modalWrapCloseAnimateEnd);
        }
    };
    disableBodyScroll = () => {
        const $body = document.body;
        const { prefix } = this.context;
        const prefixedText = prefix ? `${prefix}-` : '';
        if (!$body.classList.contains(`${prefixedText}modal-shown`)) {
            requestAnimationFrame(() => {
                const scrollbarWidth = getScrollbarWidth();
                if (window.innerHeight < $body.offsetHeight &&
                    scrollbarWidth !== 0) {
                    $body.style.marginRight = `${scrollbarWidth}px`;
                }
                $body.classList.add(`${prefixedText}modal-shown`);

            });
        }
    };
    onModalOpening = () => {
        const { prefix } = this.context;
        const prefixedText = prefix ? `${prefix}-` : '';
        if (this.$modalMain.current) {
            const $modalMain = this.$modalMain.current;
            $modalMain.classList.add(`${prefixedText}c-modal__main--isOpening`);
            if (getAnimationEndEvent) {
                $modalMain.addEventListener(getAnimationEndEvent, this.modalMainOpenAnimateEnd);
            }
        }
        if (this.$modalWrap.current) {
            const $modalWrap = this.$modalWrap.current;
            $modalWrap.classList.add(`${prefixedText}c-modals--isOpening`);
            if (getAnimationEndEvent) {
                $modalWrap.addEventListener(getAnimationEndEvent, this.modalWrapOpenAnimateEnd);
            }
        }
    };
    modalOpenerMain = () => {
        if (checkDocument) {
            this.disableBodyScroll();
        }
        this.setState({
            modalOpen: true,
        }, this.onModalOpening);
    };
    modalCloserMain = () => {
        const { prefix } = this.context;
        const prefixedText = prefix ? `${prefix}-` : '';
        document.removeEventListener('keyup', this.listenToEscape);
        if (this.$modalMain.current) {
            const $modalMain = this.$modalMain.current;
            $modalMain.classList.add(`${prefixedText}c-modal__main--isClosing`);
            if (getAnimationEndEvent) {
                $modalMain.addEventListener(getAnimationEndEvent, this.modalMainCloseAnimateEnd);
            }
        }
        else {
            this.enableBodyScroll();
            this.onModalClosedNShutDown();
        }
        if (this.$modalWrap.current) {
            const $modalWrap = this.$modalWrap.current;
            $modalWrap.classList.add(`${prefixedText}c-modals--isClosing`);
            if (getAnimationEndEvent) {
                $modalWrap.addEventListener(getAnimationEndEvent, this.modalWrapCloseAnimateEnd);
            }
        }
        else {
            this.enableBodyScroll();
            this.onModalClosedNShutDown();
        }
    };
    closeThisModal = () => {
        const { modalClose } = this.props;
        if (typeof modalClose === 'function') {
            modalClose();
        }
        else {
            this.modalCloserMain();
        }
    };
    render() {
        const { footer, customFooterLeft, openMoi, wide, small, hideHeader, header, bodyClassName, anyHeight, main, children, customFooter, customWrapper, clickAnywhere = true, } = this.props;
        const { prefix } = this.context;
        const prefixedText = prefix ? `${prefix}-` : '';
        const { modalOpen } = this.state;
        const footerExists = Array.isArray(footer) && footer.length > 0;
        const customFooterExists = typeof customFooter !== 'undefined';
        const customFooterLeftExists = typeof customFooterLeft !== 'undefined';
        const onlyCustomFooterExists = customFooterExists && !footerExists && !customFooterLeftExists;
        if (modalOpen) {
            return (React__default.createElement(Portalify, { id: "modal-container" },
                React__default.createElement("div", { ref: this.$modalWrap, className: `${prefixedText}c-modals ${openMoi ? `${prefixedText}c-modals--open` : ''}` },
                    React__default.createElement("div", { className: `${prefixedText}c-modal__bg`, title: clickAnywhere
                            ? 'Click Anywhere Here to Close'
                            : undefined, onClick: () => clickAnywhere ? this.closeThisModal() : '' }),
                    React__default.createElement("div", { className: classNames('c-modal__main overflow-hidden flex flex-col',

                        {
                            'c-modal--wide': wide,
                        }, {
                            'c-modal--small': small,
                        }, {

                        }, customWrapper), role: "dialog", "aria-modal": openMoi, ref: this.$modalMain, "aria-labelledby": `${typeof header === 'string'
                            ? header
                            : 'modal header contents'}`, "aria-describedby": `${typeof header === 'string'
                            ? header
                            : 'modal header'}` },
                        !hideHeader && (React__default.createElement("div", { className: `${header
                                ? `${prefixedText}c-window__header--type2 ${prefixedText}uppercase`
                                : `${prefixedText}c-window__header--mini ${prefixedText}mt-8`} ${prefixedText}borderBox` },
                            header ? (React__default.createElement("div", { className: `${prefixedText}mr-10` }, header || 'Header')) : null,
                            React__default.createElement("button", { className: `${prefixedText}c-window__close ${prefixedText}text-gray-500 ${prefixedText}outline-none ${prefixedText}focus:outline-none`, onClick: this.closeThisModal, ref: this.closeModalToFocus, "aria-label": "close", onKeyUp: e => {
                                    if (e.charCode === 13 ||
                                        e.charCode === 32) {
                                        this.closeThisModal();
                                    }
                                }, type: "button" }, "\u00D7"))),
                        React__default.createElement("div", { className: `${bodyClassName || `${prefixedText}p-8`} ${prefixedText}c-modal__wrapper ${anyHeight
                                ? ''
                                : `${prefixedText}c-modal__wrapper--scrollable`}` },
                            React__default.createElement("div", null,
                                React__default.createElement(If, { condition: typeof main === 'string' },
                                    React__default.createElement(Then, null,
                                        React__default.createElement("div", { dangerouslySetInnerHTML: {
                                                __html: Harpic(main || ''),
                                            } })),
                                    React__default.createElement(Else, null,
                                        React__default.createElement(If, { condition: typeof main !== 'undefined' },
                                            React__default.createElement(Then, null, main),
                                            React__default.createElement(Else, null, children)))))),
                        React__default.createElement(If, { condition: onlyCustomFooterExists },
                            React__default.createElement(Then, null, customFooter),
                            React__default.createElement(Else, null,
                                React__default.createElement(If, { condition: footerExists ||
                                        customFooterExists ||
                                        customFooterLeftExists },
                                    React__default.createElement(Then, null,
                                        React__default.createElement("div", { className: `${prefixedText}c-modal__footerContent ${prefixedText}flex ${(footerExists &&
                                                `${prefixedText}pb-4`) ||
                                                ''}` },
                                            customFooterLeftExists ? (React__default.createElement("div", { className: `${prefixedText}flex ${prefixedText}justify-start ${prefixedText}flex-row ${prefixedText}flex-wrap ${prefixedText}items-start ${prefixedText}mr-4` }, customFooterLeft)) : null,
                                            React__default.createElement("div", { className: `${prefixedText}flex ${prefixedText}ml-auto ${prefixedText}justify-end ${prefixedText}flex-row ${prefixedText}flex-wrap ${prefixedText}items-start` },
                                                customFooter,
                                                footer && footerExists
                                                    ? Array.from(footer).map((item, index) => {
                                                        const isLastItem = footer.length ===
                                                            index +
                                                                1;
                                                        const isFirstItem = index ===
                                                            0;
                                                        switch (item.type) {
                                                            case 'close':
                                                                return (React__default.createElement(Button, { key: index, link: item.link, href: item.href, color: item.color, className: `${prefixedText}c-btn--size2 ${(!isFirstItem &&
                                                                        `${prefixedText}ml-4`) ||
                                                                        ''} ${(!isLastItem &&
                                                                        `${prefixedText}mb-4 ${prefixedText}sm:mb-0 `) ||
                                                                        ''}`, variant: item.variant ||
                                                                        (!item.color &&
                                                                            'minimal') ||
                                                                        undefined, focus: item.focus, onClick: () => {
                                                                        if (typeof item.fn ===
                                                                            'function') {
                                                                            item.fn();
                                                                        }
                                                                        this.closeThisModal();
                                                                    }, form: item.form, type: item.form
                                                                        ? 'submit'
                                                                        : 'button', triangle: item.triangle, disabled: item.disabled, rel: item.rel, target: item.target }, item.text ||
                                                                    'Close'));
                                                            default:
                                                                return (React__default.createElement(Button, { key: index, color: item.color, variant: item.variant ||
                                                                        (!item.color &&
                                                                            'minimal') ||
                                                                        undefined, triangle: item.triangle, className: classNames(`${prefixedText}c-btn--size2`, {
                                                                        [`${prefixedText}mb-4 ${prefixedText}sm:mb-0`]: !isLastItem,
                                                                    }, {
                                                                        [`${prefixedText}ml-4`]: !isFirstItem,
                                                                    }), focus: item.focus, onClick: () => {
                                                                        if (typeof item.fn ===
                                                                            'function') {
                                                                            item.fn();
                                                                        }
                                                                    }, link: item.link, href: item.href, rel: item.rel, target: item.target, form: item.form, disabled: item.disabled, type: item.form
                                                                        ? 'submit'
                                                                        : 'button' }, item.text));
                                                        }
                                                    })
                                                    : null))))))))));
        }
        return React__default.createElement(Portalify, { id: "modal-container" });
    }
}
Modal.contextType = BhThemeContext;

const ExpandCollapse = memo(({ wrapperClassName, heading, children, isFar, onToggle, headerClass = '', nonExpandedWrapperClass = '', expandedHeaderClass = '', nonExpandedHeaderClass = '', expandedWrapperClass = '', noSeparatorBorder, isExpanded, innerWrapperClass = '', separatorClass = '', }) => {
    const [expanded, toggleExpand] = useState(isExpanded);
    const { prefix } = useTheme();
    const prefixedText = prefix ? `${prefix}-` : '';
    return (React__default.createElement("div", { className: `${wrapperClassName} ${expanded ? expandedWrapperClass : nonExpandedWrapperClass}` },
        React__default.createElement("div", { className: `${prefixedText}pb-4 ${prefixedText}font-bold ${prefixedText}flex ${prefixedText}items-center ${isFar ? `${prefixedText}justify-between` : ''} ${prefixedText}cursor-pointer ${prefixedText}select-none ${headerClass || ''} ${expanded ? expandedHeaderClass : nonExpandedHeaderClass}`, onClick: e => {
                if (typeof onToggle === 'function') {
                    onToggle(e);
                }
                toggleExpand(!expanded);
            } },
            heading,
            React__default.createElement(Button, { variant: "minimal", type: "button", size: "vtiny", className: `${prefixedText}ml-4 ${prefixedText}p-2 ${prefixedText}hover-opacity` }, expanded
                ? String.fromCharCode(9650)
                : String.fromCharCode(9660))),
        React__default.createElement("div", { className: `${(!expanded && `${prefixedText}hidden`) || ''} ${innerWrapperClass}` },
            !noSeparatorBorder && (React__default.createElement("div", { className: separatorClass ||
                    `${prefixedText}border-b ${prefixedText}border-separatorColor"` })),
            children)));
});

const TabItems = ({ current, titleClassName, currentClassName, shiftTab, changeCurrent, index, item, }) => {
    const isSelected = current === index;
    if (typeof item === 'string') {
        return (React__default.createElement("button", { className: `${titleClassName} ${(isSelected && currentClassName) || ''}`, role: "tab", type: "button", onClick: e => changeCurrent(e, index), onKeyUp: shiftTab, "aria-selected": isSelected, "aria-label": item, key: item }, item));
    }
    const { label, children, disabled } = item;
    return (React__default.createElement("button", { className: `${titleClassName} ${(isSelected && currentClassName) || ''}`, role: "tab", type: "button", disabled: disabled, onClick: e => changeCurrent(e, index), onKeyUp: shiftTab, "aria-selected": isSelected, "aria-label": label, key: label }, children || label));
};
class Tabs extends PureComponent {
    selectorStyle = {
        transform: 'scaleX(0)',
    };
    $tabSelector = createRef();
    $tabWrapper = createRef();
    constructor(props) {
        super(props);
        this.state = {
            mainClassname: 'c-tabs',
            titleClassName: 'c-tabs__tab',
        };
    }
    componentDidMount() {
        this.assignClassName();
        if (this.$tabSelector.current) {
            this.alignSelector();
            window.addEventListener('resize', this.alignSelector);
        }
    }
    componentDidUpdate() {
        if (this.$tabSelector.current) {
            this.alignSelector();
        }
    }
    componentWillUnmount() {
        if (this.$tabSelector.current) {
            window.removeEventListener('resize', this.alignSelector);
        }
    }
    assignClassName = () => {
        const { className, fullWidth, type, titleClass, noSelector } = this.props;
        this.setState({
            mainClassname: classNames(className, 'c-tabs', {
                'c-tabs--noSelector': noSelector,
                'c-tabs--capsule': type === 'capsule',
                'c-tabs--hollow': type === 'hollow',
            }),
            titleClassName: classNames(titleClass, 'c-tabs__tab', {
                'c-tabs__tab--fullWidth': fullWidth,
            }),
        });
    };
    setSelector = (target) => {
        const elLayoutPpops = target.getBoundingClientRect();
        this.$tabSelector.current.style.transform = `translateX(${target.offsetLeft}px)scaleX(${elLayoutPpops.width / 100})`;
    };
    changeCurrent = (e, index) => {
        const { dontBubble, noSelector, chooseCurrent } = this.props;
        if (e) {
            const { currentTarget } = e;
            if (dontBubble) {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
            }
            if (!noSelector && !!this.$tabSelector.current) {
                this.setSelector(currentTarget.closest('.c-tabs__tab'));
            }
        }
        chooseCurrent(+index);
    };
    alignSelector = () => {
        requestAnimationFrame(() => {
            this.setSelector(this.$tabWrapper.current.querySelector('.current'));
        });
    };
    shiftTab = (e) => {
        const { chooseCurrent, current, tabTitles, noSelector } = this.props;
        if (e.currentTarget) {
            const { nextSibling, previousSibling, parentNode } = e.currentTarget;
            if (e.key === 'ArrowRight') {
                const validNextElem = nextSibling &&
                    nextSibling.classList.contains('c-tabs__tab');
                const nextIndex = validNextElem ? current + 1 : 0;
                const nextElem = ((validNextElem && nextSibling) ||
                    parentNode?.querySelector('.c-tabs__tab'));
                nextElem?.focus();
                if (!noSelector && !!this.$tabSelector.current) {
                    this.setSelector(nextElem);
                }
                chooseCurrent(nextIndex);
                return;
            }
            if (e.key === 'ArrowLeft') {
                const validPrevElem = previousSibling &&
                    previousSibling.classList.contains('c-tabs__tab');
                const lastTabElem = tabTitles.length - 1;
                const prevIndex = validPrevElem ? current - 1 : lastTabElem;
                const prevElem = ((validPrevElem && previousSibling) ||
                    parentNode.querySelectorAll('.c-tabs__tab')[lastTabElem]);
                prevElem.focus();
                if (!noSelector && !!this.$tabSelector.current) {
                    this.setSelector(prevElem);
                }
                chooseCurrent(prevIndex);
                return;
            }
        }
    };
    render() {
        const { tabTitles, titleClassCurrent, current, reverse, noSelector, wrapperClass, } = this.props;
        const { mainClassname, titleClassName } = this.state;
        const currentClassName = `current ${titleClassCurrent || '' || ''}`;
        const tabTitlewas = reverse ? tabTitles.reverse() : tabTitles;
        return (React__default.createElement("div", { role: "tablist", className: mainClassname },
            React__default.createElement("div", { className: `c-tabs__tabWrapper ${(!noSelector && 'overflow-hidden') || ''} whitespace-no-wrap ${wrapperClass || ''}`, ref: this.$tabWrapper },
                !noSelector && (React__default.createElement("div", { className: "c-tabs__tabSelector", style: this.selectorStyle, ref: this.$tabSelector })),
                tabTitlewas &&
                    tabTitlewas.length > 0 &&
                    tabTitlewas.map((tabTitleItem, index) => (React__default.createElement(TabItems, { current: current, titleClassName: titleClassName, currentClassName: currentClassName, shiftTab: this.shiftTab, changeCurrent: this.changeCurrent, index: index, item: tabTitleItem, key: typeof tabTitleItem === 'string'
                            ? tabTitleItem
                            : tabTitleItem.label }))))));
    }
}

const Accordion = ({ label, children, isExpanded, labelClassName = '', labelOnOpenClassName = '', noExpanderLeft = false, expanderLeft = null, expanderRight = null, rotatingExpanderLeft = null, rotatingExpanderRight = null, onOpen, onClose, onToggle, }) => {
    const { prefix } = useTheme();
    const prefixText = prefix ? `${prefix}-` : '';
    const openAnimationFrameRunner = useRef({
        getRafVal: () => 0,
    });
    const closeAnimationFrameRunner = useRef({
        getRafVal: () => 0,
    });
    const [isOpen, open] = useState(isExpanded || false);
    const ansBox = useRef(null);
    const setWhenToggled = useCallback((op) => {
        const $ansBox = ansBox.current;
        if ($ansBox) {
            if (op) {

                if (checkDocument &&
                    hasOwn(window, 'cancelAnimationFrame') &&
                    'getRafVal' in closeAnimationFrameRunner.current) {
                    window.cancelAnimationFrame(closeAnimationFrameRunner.current.getRafVal());
                }
                $ansBox.classList.remove(`${prefixText}h-0`);
                const height = $ansBox.offsetHeight;
                $ansBox.classList.add(`${prefixText}h-0`);
                openAnimationFrameRunner.current = smoothAnimate((ht) => {
                    $ansBox.style.height = `${ht}px`;
                }, 0, height, 0, 0.1, () => {
                    $ansBox.style.height = '';
                    $ansBox.classList.remove(`${prefixText}h-0`);
                });
                if (typeof onOpen === 'function') {
                    onOpen();
                }
            }
            else {

                if (checkDocument &&
                    hasOwn(window, 'cancelAnimationFrame')) {
                    window.cancelAnimationFrame(openAnimationFrameRunner.current.getRafVal());
                }
                const height = $ansBox.offsetHeight;
                closeAnimationFrameRunner.current = smoothAnimate((ht) => {
                    $ansBox.style.height = `${ht}px`;
                }, height, 0, height, 0.1, () => {
                    $ansBox.style.height = '';
                    $ansBox.classList.add(`${prefixText}h-0`);
                });
                if (typeof onClose === 'function') {
                    onClose();
                }
            }
            if (typeof onToggle === 'function') {
                onToggle(op);
            }
        }
    }, [prefixText, onOpen, onClose, onToggle]);
    const opener = useCallback(() => {
        open(op => {
            setWhenToggled(!op);
            return !op;
        });
    }, [setWhenToggled]);
    useLayoutEffect(() => {
        setWhenToggled(isOpen);

    }, []);
    useEffect(() => {
        setWhenToggled(Boolean(isExpanded));
        open(Boolean(isExpanded));
    }, [isExpanded]);
    const rotatingExpanderLeftRender = rotatingExpanderLeft ||
        (!noExpanderLeft && (React__default.createElement("svg", { width: "23", height: "23", xmlns: "http://www.w3.org/2000/svg", className: `${prefixText}rounded-full ${prefixText}mr-4 ${prefixText}border ${prefixText}border-orange-300 ${prefixText}mt-1 ${prefixText}inline-block`, style: {
                transition: 'transform 0.1s ease-out',
                transform: `rotate(${isOpen ? '0deg' : '-90deg'})`,
            } },
            React__default.createElement("g", { transform: "rotate(90 11.5 11.5)", fillRule: "nonzero", fill: "none" },
                React__default.createElement("circle", { fill: "#FFF7D9", cx: "11.5", cy: "11.5", r: "11.5" }),
                React__default.createElement("path", { d: "M9.7 15l3.6-3.5-3.6-3.6a.9.9 0 010-1.3 1 1 0 011.3 0l4.3 4.2a1 1 0 010 1.4L11 16.4a1 1 0 01-1.3 0 .9.9 0 010-1.3z", fill: "#E7A500" })))));
    return (

    React__default.createElement(Fragment, null,
        React__default.createElement("button", { className: `${prefixText}border border-transparent ${prefixText}select-none ${prefixText}p-3 ${prefixText}flex ${prefixText}items-start ${prefixText}yellow-icon ${prefixText}cursor-pointer ${isOpen ? labelOnOpenClassName : ''} ${labelClassName}`, style: {
                transition: 'background 0.1s ease-out',
            }, onClick: opener, "aria-expanded": isOpen ? 'true' : 'false', type: "button" },
            expanderLeft,
            rotatingExpanderLeftRender,
            React__default.createElement("h4", { className: `${prefixText}leading-relaxed ${prefixText}mt-1` }, label),
            expanderRight,
            rotatingExpanderRight && (React__default.createElement("div", { style: {
                    transition: 'transform 0.1s ease-out',
                    transform: `rotate(${isOpen ? '0deg' : '-90deg'})`,
                } }, rotatingExpanderRight))),
        React__default.createElement("div", { ref: ansBox, className: `${prefixText}overflow-hidden ${prefixText}h-0` }, children))

    );
};
Accordion.defaultProps = {
    labelOnOpenClassName: '',
    isExpanded: false,
    labelClassName: '',
};
var index = memo(Accordion);

const YoutubeBtn = memo(({ className, iconOnly, text, btnProps = {
    variant: 'minimal',
}, onClick, }) => {
    const { prefix } = useTheme();
    const prefixText = prefix ? `${prefix}-` : '';
    return (React__default.createElement(Button, { ...btnProps, onClick: onClick, className: `${prefixText}flex ${prefixText}text-left ${prefixText}items-center ${className || ''}` },
        React__default.createElement("div", { className: `${prefixText}flex ${prefixText}items-start` },
            React__default.createElement("div", { className: `${prefixText}mr-2 ${prefixText}u-text--singleLine ${prefixText}text-redLighter` },
                React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", "data-name": "Layer 1", height: "13", className: `${prefixText}-mt-1 ${prefixText}inline-block ${prefixText}align-middle`, viewBox: "0 0 24 24" },
                    React__default.createElement("path", { fill: "#ff5746", d: "M12 0a12 12 0 1 0 12 12A12 12 0 0 0 12 0zM9.6 17.4V6.6l7.2 5.4z" })),
                ' ',
                !iconOnly && text ? 'Watch:' : 'Watch Now'),
            ' ',
            text)));
});

class YoutubePlayerMain extends PureComponent {

    _$ytPlay = createRef();
    $ytplayMain = createRef();
    ytImgIObserver;
    _source = '';
    constructor(props) {
        super(props);
        this.$ytplayMain = (this.props.forwardedRef ||
            createRef());
    }
    componentDidMount() {
        if ('IntersectionObserver' in window) {
            this.ytImgIObserver = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        if (this.ytImgIObserver) {

                            const entryTarget = entry.target;
                            this.ytImgIObserver.unobserve(entryTarget);
                            if (entryTarget.dataset.src) {
                                entryTarget.src = entryTarget.dataset.src;
                            }
                        }
                    }
                });
            }, {
                rootMargin: '50px',
                threshold: 0.01,
            });
        }
        this._loadVideo();
        if (this.$ytplayMain.current) {
            this.$ytplayMain.current.addEventListener('click', this._playVideo);
        }
    }
    componentWillUnmount() {
        if (this.$ytplayMain.current) {
            this.$ytplayMain.current.removeEventListener('click', this._playVideo);
        }
    }
    _loadVideo = () => {
        this._source = `https://img.youtube.com/vi/${this.props.vid}/sddefault.jpg`;
        const image = new Image();
        image.setAttribute('data-src', this._source);
        image.alt = 'Check this out';
        if (this._$ytPlay.current) {
            this._$ytPlay.current.appendChild(image);
            if (this._$ytPlay.current.querySelector('img')) {
                this.ytImgObserver(this._$ytPlay.current.querySelector('img'), this._source);
            }
        }
    };
    _playVideo = () => {
        const { vid, start, onStart } = this.props;
        const iframe = document.createElement('iframe');
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-presentation');
        iframe.setAttribute('allow', 'encrypted-media;autoplay');
        iframe.setAttribute('src', `https://www.youtube.com/embed/${vid}?rel=0&showinfo=0&autoplay=1${!!start ? `&start=${start}` : ''}`);
        if (this._$ytPlay.current) {
            this._$ytPlay.current.innerHTML = '';
            this._$ytPlay.current.appendChild(iframe);
        }
        if (typeof onStart === 'function') {
            onStart();
        }
    };
    ytImgObserver = (image, source) => {
        if (!this.ytImgIObserver) {
            image.src = source;
        }
        else {
            this.ytImgIObserver.observe(image);
        }
    };
    render() {
        const { prefix } = this.context;
        const prefixedText = prefix ? `${prefix}-` : '';
        const { wrapperClass } = this.props;
        return (React__default.createElement("div", { className: `${prefixedText}videoWrapper ${wrapperClass || ''}`, ref: this.$ytplayMain },
            React__default.createElement("div", { className: `${prefixedText}c-ytPlay ${prefixedText}aspect-ratio--object ${prefixedText}cover ${this.props.cinema
                    ? `${prefixedText}c-ytPlay--cinema`
                    : ''}`, ref: this._$ytPlay },
                React__default.createElement("div", { className: `${prefixedText}yt__play-button` }))));
    }
}
YoutubePlayerMain.contextType = BhThemeContext;
const YoutubePlayer = forwardRef((props, ref) => {
    return React__default.createElement(YoutubePlayerMain, { ...props, forwardedRef: ref });
});

class Draggable extends PureComponent {
    drawer__wrapper = createRef();
    drawer__main = createRef();
    width__main = 0;
    startX = 0;
    startY = 0;
    currentX = 0;
    currentY = 0;
    touchingSideNav = false;
    transitionEndProperty = null;
    transitionEndTime = 0;
    continueY = null;
    componentDidMount() {
        const { isOpened } = this.props;
        if (isOpened) {
            this.showSideNav();
        }
    }
    componentDidUpdate(prevProps) {
        const { isOpened } = this.props;
        if (prevProps.isOpened !== isOpened) {
            if (isOpened) {
                this.showSideNav();
            }
            else {
                this.hideSideNav();
            }
        }
    }
    componentWillUnmount() {
        if (checkDocument) {
            const $body = document.body;
            document.removeEventListener('keyup', this.listenToEscape);
            $body.classList.remove('bb-drawer-shown');

            if (getScrollbarWidth() !== 0) {
                $body.style.marginRight = '';
            }
        }
    }
    onTransitionEnd = (evt) => {

        if (evt.propertyName !== this.transitionEndProperty &&
            evt.elapsedTime !== this.transitionEndTime) {
            return;
        }
        if (checkDocument) {
            const { isOpened } = this.props;
            document.addEventListener('keyup', this.listenToEscape);
            if (isOpened) {
                this.disableBodyScroll();
            }
            else {
                document.removeEventListener('keyup', this.listenToEscape);
                requestAnimationFrame(() => {
                    this.enableBodyScroll();
                    this.drawer__wrapper.current?.classList.add('pointer-events-none');
                });
            }
        }
        this.transitionEndProperty = null;
        this.transitionEndTime = 0;
        this.drawer__wrapper.current?.classList.remove('bb-drawer--animatable');

        this.drawer__wrapper.current?.removeEventListener(getTransitionEndEvent, this.onTransitionEnd);
    };
    disableBodyScroll = () => {
        const $body = document.body;
        const scrollbarWidth = getScrollbarWidth();
        if (window.innerHeight < $body.offsetHeight && scrollbarWidth !== 0) {
            $body.style.marginRight = `${scrollbarWidth}px`;
        }
        $body.classList.add('bb-drawer-shown');

    };
    enableBodyScroll = () => {
        const $body = document.body;
        $body.classList.remove('bb-drawer-shown');

        if (window.innerHeight < $body.offsetHeight &&
            getScrollbarWidth() !== 0) {
            $body.style.marginRight = '';
        }
    };
    showSideNav = () => {

        this.drawer__wrapper.current?.classList.add('bb-drawer--animatable', 'bb-drawer--open');
        this.drawer__wrapper.current?.classList.remove('pointer-events-none');

        this.transitionEndProperty = 'transform';

        this.transitionEndTime = 0.33;
        if (getTransitionEndEvent) {
            this.drawer__wrapper.current?.addEventListener(getTransitionEndEvent, this.onTransitionEnd);
        }
    };
    hideSideNav = () => {
        this.drawer__wrapper.current?.classList.remove('bb-drawer--open');
        this.drawer__wrapper.current?.classList.add('bb-drawer--animatable');

        this.transitionEndProperty = 'transform';

        this.transitionEndTime = 0.13;
        if (getTransitionEndEvent) {
            this.drawer__wrapper.current?.addEventListener(getTransitionEndEvent, this.onTransitionEnd);
        }
    };
    listenToEscape = (e) => {
        if (e.key === 'Escape') {
            this.props.closeSidenav();
        }
    };
    onTouchStart = (e) => {
        e.stopPropagation();
        const { isOpened } = this.props;
        if (!isOpened)
            return;
        this.width__main =
            this.drawer__main.current?.getBoundingClientRect().width || 0;
        this.startX = e.touches[0].pageX;
        this.startY = e.touches[0].pageY;
        this.currentX = this.startX;
        this.currentY = this.startY;
        requestAnimationFrame(this.update);
        this.touchingSideNav = true;
    };
    onTouchMove = (e) => {
        const { isOpened } = this.props;
        if (!isOpened && !this.touchingSideNav)
            return;
        this.currentX = e.touches[0].pageX;
        this.currentY = e.touches[0].pageY;
        if (this.continueY === null) {
            const translateX = Math.abs(this.currentX - this.startX);
            const translateY = Math.abs(this.currentY - this.startY);
            if (translateY < translateX) {
                this.continueY = false;
                this.drawer__main.current.style.overflow = 'hidden';
            }
            else {
                this.continueY = true;
                this.touchingSideNav = false;
                this.drawer__main.current.style.transform = '';
            }
        }
    };
    onTouchEnd = () => {
        const { pos, isOpened, closeSidenav } = this.props;
        if (!isOpened && !this.touchingSideNav)
            return;
        this.touchingSideNav = false;
        this.continueY = null;
        this.drawer__main.current.style.transform = '';
        this.drawer__main.current.style.overflow = '';
        const translateX = Math.abs(Math.min(pos === 'left'
            ? Math.min(0, this.currentX - this.startX)
            : Math.max(0, this.currentX - this.startX)));
        if (translateX > this.width__main / 3) {
            closeSidenav();
        }
    };
    update = () => {
        if (!this.touchingSideNav) {
            return;
        }
        const { pos } = this.props;
        const translateX = pos === 'left'
            ? Math.min(0, this.currentX - this.startX)
            : Math.max(0, this.currentX - this.startX);
        this.drawer__main.current.style.transform = `translate3d(${translateX}px, 0, 0)`;
        requestAnimationFrame(this.update);
    };
    blockClicks = (evt) => {
        evt.stopPropagation();
    };
    render() {
        const { pos, width, closeSidenav, isOpened, ...childProps } = this.props;
        return (React__default.createElement("aside", { className: "bb-drawer pointer-events-none", ref: this.drawer__wrapper, onClick: closeSidenav, onTouchStart: this.onTouchStart, onTouchMove: this.onTouchMove, onTouchEnd: this.onTouchEnd, ...childProps },
            React__default.createElement("div", { className: classNames({
                    'bb-drawer__main--left': pos === 'left',
                }, {
                    'bb-drawer__main--right': pos === 'right',
                }, 'bb-drawer__main', 'scrolling-touch', 'bb-no-scroll-bar'), style: {
                    width: width ? `${width}px` : '90wv',
                    maxWidth: '90vw',
                }, ref: this.drawer__main, onClick: this.blockClicks }, childProps.children)));
    }
}

export { index as Accordion, BhThemeConsumer, BhThemeContext, BhThemeProvider, Bulleted, Button, Case, CircleNumbered, Default, Draggable as DraggableSideNav, Else, ExpandCollapse, If, IfElse, bars as LoaderBars, linearDots as LoaderLinearDots, LoaderLinearDotsPlain, puff as LoaderPuff, smallPulse as LoaderSmallPulse, Modal, Portalify, Switch, Tabs, Then, Tooltip, YoutubeBtn, YoutubePlayer, useTheme };
